/**
 * FolderSidebar — renders the folder tree with matters nested under each
 * folder, plus an Unfiled section for matters in zero folders. Used as
 * the primary matter-navigation sidebar in Workspace.
 *
 * Open/closed state per-folder is per-workstation, persisted to
 * localStorage keyed by client code.
 *
 * Matter selection: clicking a matter sets the selected matter ID; the
 * folder ID it was clicked under is also captured (URL state in
 * Workspace, not here).
 *
 * Folder management: hover a folder row → see + (add child) / pencil
 * (rename) / trash icons. Hover a matter row → see folder icon to
 * open the picker.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode, type MouseEvent as ReactMouseEvent, type FocusEvent as ReactFocusEvent, type PointerEvent as ReactPointerEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, Folder as FolderIcon, FolderInput, Trash2, Plus, Pencil, Archive, RotateCcw, ArrowDownAZ, Clock, MoreHorizontal } from 'lucide-react'
import { traceSort, traceDestination } from '../tracking/searchTrace'
import { sidebarUserInteracted, markSidebarInteracted } from '../sidebarSession'
import { folders as foldersApi, matters as mattersApi, purgeMatter, type FolderNode, type Matter, type MatterCounter } from '../api'
import { useCounterWindow, saveCounterWindow, windowRequestParams, windowKey, windowLabel, windowToHours, windowFromHours, roundHours, relLabel, type CounterWindow, type WindowPreset } from '../counterWindow'
import { useProductName } from '../productName'
import { isPlayheadTenant } from '../playhead'
import { getCategoryBandStyle } from '../uiConfig'
import { Glyph } from '../glyphs'

// Category band label style (Ken 2026-10-23). Driven by ui_config.pane1
// .category_band_style: 'filled' = the pill (fill + padding + radius); 'none' =
// bare label, no box. Both colour tokens stay defined in the theme either way.
function bandPillStyle(): Record<string, string | number> {
  // fontFamily is the pane-1-only --me-font-category hook (defaults to the Chrome
  // font; KEN can override it in ui_config.theme to give just the top-level category
  // labels a different typeface). Only the KEN/playhead category spans call this, so
  // MOVU is untouched (Ken 2026-10-26).
  // UXT-7 7C: category labels = navigational heading voice (weight 500, #57534e)
  // — heavier + darker than the system-label caption (400, #a8a29e). Same Arial,
  // 11.5px, uppercase, .11em tracking; separated only by weight + colour.
  const base = { fontFamily: 'var(--me-font-category)', fontSize: 'var(--me-font-category-size, 10.5px)', fontWeight: 500, letterSpacing: '0.11em', textTransform: 'uppercase', color: 'var(--me-band-label)' }
  return getCategoryBandStyle() === 'filled'
    ? { ...base, padding: '5px 9px', borderRadius: 4, background: 'var(--me-band-fill)' }
    : base
}
import { useFlashOnChange } from './useFlashOnChange'
import { useAuth } from '../auth'
import { openSecretaryChat, SecretaryChatHost } from './SecretaryChat'

type Props = {
  matters: Matter[]
  selectedMatterId: number | 'inbox' | null
  selectedFolderId: number | null
  onSelectMatter: (matterId: number, folderId: number | null) => void
  onArchiveMatter?: (matterId: number) => void
  /** Total active-matter count for the header-row counter (unfiltered by
   *  search — the stable "N matters" total). */
  matterCount?: number
  clientCode: string
  /** True when the user has typed in the matter search box. We auto-expand
   *  folders that contain matching matters so the results are visible.
   *  When search clears, folders return to their pre-search state because
   *  the search-driven expansion is derived (not persisted) — only the
   *  user's manual toggles touch localStorage. If during search the user
   *  clicks a matter, the selection effect adds its ancestors to the
   *  persisted openSet, so those folders stay open after clearing search. */
  searchActive?: boolean
  /** Clears the pane-1 search box. The clock button uses it to leave a
   *  search-instantiated view straight into 'What's New' (Ken 2026-08-18). */
  onClearSearch?: () => void
  /** Selecting the clock ("What's New") collapses the Archived section (which
   *  lives in the Workspace parent). Called on entering affected mode. */
  onEnterAffected?: () => void
  /** Set of matter ids that the user has already "seen" — initial value
   *  is whatever existed at page load. A brand-new row (id not in this
   *  set) flashes on first mount; the row calls markSeen() afterward so
   *  a subsequent unmount/remount (folder collapse/expand) doesn't
   *  re-fire. Null means "snapshot not yet captured" — treat everything
   *  as already-seen for now. */
  seenAtLoad?: Set<number> | null
  /** Callback to mark a matter id as "seen" once its flash has played.
   *  Called from MatterRowCompact when isNew=true and the row mounts. */
  markSeen?: (id: number) => void
}

const FOLDER_OPEN_KEY_PREFIX = 'me.folderOpen.'
const UNFILED_OPEN_KEY_PREFIX = 'me.unfiledOpen.'

function loadOpenSet(clientCode: string): Set<number> {
  try {
    const raw = localStorage.getItem(FOLDER_OPEN_KEY_PREFIX + clientCode)
    if (!raw) return new Set()
    const arr = JSON.parse(raw)
    return new Set(Array.isArray(arr) ? arr.filter((n) => typeof n === 'number') : [])
  } catch { return new Set() }
}

function saveOpenSet(clientCode: string, openSet: Set<number>): void {
  try {
    localStorage.setItem(FOLDER_OPEN_KEY_PREFIX + clientCode, JSON.stringify([...openSet]))
  } catch { /* ignore */ }
}

// ── Row tool-reveal interaction (Ken 2026-08-10) ──────────────────────────
// Kills the "every hover flashes tools" noise on sidebar rows. Tools reveal
// only when intent shows, two ways:
//   1. Dwell — the pointer RESTS on the row ~600ms. Every move restarts the
//      timer, so a moving pointer (browsing) never triggers it; stopping does.
//   2. Tool zone — the pointer enters the row's right-hand portion. Names are
//      on the left, tools on the right, so moving right is itself intent →
//      reveal immediately, no dwell.
// The grey row background stays immediate (CSS :hover, untouched). No "warm"
// state: each row dwells independently, so revealing one never primes the next.
const DWELL_MS = 1600
const TOOL_ZONE_PX = 95  // right-hand tool zone width
function useDwellReveal() {
  const [revealed, setRevealed] = useState(false)
  const timer = useRef<number | null>(null)
  const clear = () => { if (timer.current != null) { window.clearTimeout(timer.current); timer.current = null } }
  const check = (e: ReactMouseEvent<HTMLElement>) => {
    const r = e.currentTarget.getBoundingClientRect()
    if (e.clientX >= r.right - TOOL_ZONE_PX) {
      clear()
      setRevealed(true)  // in the tool zone → reveal now
    } else if (!revealed) {
      // name zone → reveal only once the pointer stops for DWELL_MS
      clear()
      timer.current = window.setTimeout(() => setRevealed(true), DWELL_MS)
    }
  }
  const onMouseLeave = () => { clear(); setRevealed(false) }
  // Keyboard focus reveals too (Ken 2026-08-10 spec): focus into the row shows
  // its actions; focus leaving the row hides them (no lingering echo).
  const onFocus = () => setRevealed(true)
  const onBlur = (e: ReactFocusEvent<HTMLElement>) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setRevealed(false)
  }
  useEffect(() => clear, [])
  // Arrow-key nav fires this so a parked-mouse dwell echo on some OTHER row
  // clears the instant the user starts browsing by key (the newly focused row
  // re-reveals via onFocus above). Ken 2026-08-10.
  useEffect(() => {
    const onClear = () => { clear(); setRevealed(false) }
    window.addEventListener('me-row-reveal-clear', onClear)
    return () => window.removeEventListener('me-row-reveal-clear', onClear)
  }, [])
  return { revealed, rowProps: { onMouseEnter: check, onMouseMove: check, onMouseLeave, onFocus, onBlur } }
}

// ── Slow pip reveal (Ken 2026-11-01) ──────────────────────────────────────
// The KEN top-level category pips are hidden until the pointer has rested on
// the label's line/bar for PIP_DWELL_MS (4s) continuously. Unlike useDwellReveal
// this is movement-tolerant: entering the row starts the clock, moving within it
// does NOT restart, and leaving cancels + hides. So it's a true "hover here for
// four seconds" rather than "stop moving for a moment".
const PIP_DWELL_MS = 4000
function usePipHoverReveal(delayMs: number) {
  const [shown, setShown] = useState(false)
  const timer = useRef<number | null>(null)
  const clear = () => { if (timer.current != null) { window.clearTimeout(timer.current); timer.current = null } }
  const onEnter = () => { clear(); timer.current = window.setTimeout(() => setShown(true), delayMs) }
  const onLeave = () => { clear(); setShown(false) }
  useEffect(() => clear, [])
  return { shown, onEnter, onLeave }
}

export default function FolderSidebar({
  matters, selectedMatterId, selectedFolderId, onSelectMatter, onArchiveMatter, clientCode,
  matterCount, searchActive, onClearSearch, onEnterAffected, seenAtLoad, markSeen,
}: Props) {
  const tree = useQuery({
    queryKey: ['folders', 'tree'],
    queryFn: () => foldersApi.tree(),
    staleTime: 30_000,
    // Poll every 20s so folder counts and AI auto-folder placements
    // appear without requiring a manual refresh. ETag-equivalent comes
    // from the cache — react-query won't re-render unless the response
    // payload genuinely differs.
    refetchInterval: 20_000,
  })

  const qc = useQueryClient()
  // Global time-window counters: per-matter {issues, attachments, new} since
  // the user's chosen border. ONE global window (replaces the retired
  // per-folder "since you last opened it" delta). Keyed on the resolved
  // `since` date so changing the window refetches; polled like the tree.
  // Sidebar label boldness (QA Product Name tool). Applied live.
  const { boldFolder, boldSubfolder, boldMatter, unfiledLabel } = useProductName()
  const counterWindow = useCounterWindow()
  // DEMO tenant: the newness dial is a story PLAYHEAD (as-of rewind driving the
  // pane-2 chronology), not a lookback window. In that mode all matters exist
  // from the founding email onward, so the sidebar must NOT prune to a recent-
  // activity "What's New" window on grab — at the present ("now") end that window
  // is ~empty and would blank the list while pane-2 shows the full story. Keep
  // the sidebar stable for the demo; the dial only moves the pane-2 playhead.
  const playhead = isPlayheadTenant(clientCode)
  const countersQuery = useQuery({
    // Key is the window PRESET (stable), not a rolling timestamp — hour
    // windows send a relative amount and the backend resolves "now" fresh on
    // each 20s poll, so the counts stay live without the key thrashing.
    queryKey: ['matters', 'counters', windowKey(counterWindow)],
    queryFn: () => mattersApi.counters(windowRequestParams(counterWindow)),
    staleTime: 15_000,
    refetchInterval: 20_000,
  })
  const matterCounters = countersQuery.data ?? {}
  // Newest MATTER activity (max issue source_date across matters), fetched
  // window-wide, for snapping the clock to a band that actually contains
  // matters. This is the SAME signal the What's-New filter keys off. NB: neither
  // corpus_edge (max over ALL emails incl. ignored/digests → too recent, snaps
  // to an empty band) nor matter.updated_at (bumped by bulk edits) works here.
  const snapCountersQuery = useQuery({
    queryKey: ['matters', 'counters', 'snap-365d'],
    queryFn: () => mattersApi.counters({ days: 365 }),
    staleTime: 60_000,
  })

  // clientCode arrives async (after /api/auth/me resolves). Initial render
  // can have it empty. Naive useState(() => loadFromStorage(clientCode))
  // would read the WRONG key on first render, then immediately overwrite
  // the right key with the wrong (default) value — wiping persisted state.
  // Fix: track which clientCode we've initialized for via a ref, and only
  // (re)read when it changes. Save only after init completes.
  const initializedFor = useRef<string | null>(null)
  const [openSet, setOpenSet] = useState<Set<number>>(new Set())
  const [unfiledOpen, setUnfiledOpen] = useState<boolean>(true)
  // Ken 2026-07-05 / 2026-08-17: sidebar view/sort mode.
  //   'alpha'    — merged (subfolders + matters), case-insensitive by name
  //   'code_*'   — matters by M-### number (asc / desc)
  //   'affected' — "What's New": prune to matters touched since the border,
  //                in folder context, ordered by last_activity desc. (Replaced
  //                the old 'recent' clock sort, whose job the border subsumes.)
  // No persistence per Ken's spec; default 'alpha'. Re-clicking the affected
  // (clock) toggle returns to 'alpha'.
  const [sortMode, setSortMode] = useState<'alpha' | 'affected'>('alpha')
  // Relax the 'affected' (clock / What's New) view to alphabetical the moment a
  // search starts. That view is deliberately pruned to a tiny curated set, so
  // searching within it would strand the user at a blank screen; alpha searches
  // the full tree. One-way — clearing the search leaves you in alpha (re-click
  // the clock to return). (Ken 2026-08-18.)
  useEffect(() => {
    if (searchActive && sortMode === 'affected') setSortMode('alpha')
  }, [searchActive, sortMode])

  useEffect(() => {
    if (!clientCode) return
    if (initializedFor.current === clientCode) return
    initializedFor.current = clientCode
    // Hard refresh (no interaction yet this page load) → start fully collapsed;
    // restore the persisted open-set only once the user has begun interacting
    // this session, so in-app nav still remembers it (Ken 2026-08-19).
    setOpenSet(sidebarUserInteracted() ? loadOpenSet(clientCode) : new Set())
    try {
      const v = localStorage.getItem(UNFILED_OPEN_KEY_PREFIX + clientCode)
      setUnfiledOpen(v === null ? true : v === '1')
    } catch { /* default true already set */ }
  }, [clientCode])

  useEffect(() => {
    if (!clientCode || initializedFor.current !== clientCode) return
    // Don't let the fresh-load collapsed default overwrite the user's saved
    // open-set; only persist once they've actually interacted (Ken 2026-08-19).
    if (!sidebarUserInteracted()) return
    saveOpenSet(clientCode, openSet)
  }, [clientCode, openSet])

  useEffect(() => {
    if (!clientCode || initializedFor.current !== clientCode) return
    try {
      localStorage.setItem(UNFILED_OPEN_KEY_PREFIX + clientCode, unfiledOpen ? '1' : '0')
    } catch { /* ignore */ }
  }, [clientCode, unfiledOpen])

  // "Clean collapse" signal — the pane-1 ✕ button (and, if wired, Main): fully
  // reset the tree to a tidy closed state regardless of the current view. Every
  // folder collapsed, the Unfiled section collapsed, and the sort back to alpha
  // so What's-New (which force-opens its matched folders) isn't holding things
  // open. Ken 2026-08-23.
  useEffect(() => {
    const collapse = () => { setOpenSet(new Set()); setUnfiledOpen(false); setSortMode('alpha') }
    window.addEventListener('me-collapse-folders', collapse)
    return () => window.removeEventListener('me-collapse-folders', collapse)
  }, [])

  const toggle = (folderId: number) => {
    markSidebarInteracted()
    setOpenSet((prev) => {
      const next = new Set(prev)
      if (next.has(folderId)) next.delete(folderId); else next.add(folderId)
      return next
    })
  }

  // Search auto-expansion: when the user has typed in the matter
  // search box, Workspace passes the filtered matters list AND
  // searchActive=true. We compute the set of folder IDs that contain
  // any of the displayed matters (walking up to the root) and union
  // with the persisted openSet for display. This expansion is NEVER
  // persisted — clearing the search returns folders to their pre-
  // search state automatically. If the user clicks a matter while
  // searching, the selection-effect adds those ancestors to the
  // persisted set, so they stay open after clearing.
  const searchExpansion = useMemo(() => {
    if (!searchActive) return new Set<number>()
    const expanded = new Set<number>()
    const folderMap = new Map<number, FolderNode>()
    const indexNodes = (nodes: FolderNode[]) => {
      for (const n of nodes) {
        folderMap.set(n.id, n)
        if (n.children) indexNodes(n.children)
      }
    }
    indexNodes(tree.data || [])
    for (const m of matters) {
      if (m.status === 'closed') continue
      for (const a of m.folder_assignments || []) {
        let fid: number | null = a.folder
        while (fid != null) {
          expanded.add(fid)
          const parent: number | null | undefined = folderMap.get(fid)?.parent_folder
          fid = parent ?? null
        }
      }
    }
    return expanded
  }, [searchActive, matters, tree.data])

  // Combined display set passed to FolderRow. User toggles still only
  // touch openSet (persisted) — searchExpansion is a transient overlay.
  const displayedOpenSet = useMemo(() => {
    if (!searchActive) return openSet
    const out = new Set(openSet)
    for (const id of searchExpansion) out.add(id)
    return out
  }, [openSet, searchExpansion, searchActive])

  // Every folder id in the tree (top-level + nested), for the
  // collapse-all / expand-all control in the section header. The button's
  // state is derived from whether ANY folder is open (Ken 2026-08-12): as soon
  // as you open a single folder (e.g. to reach a matter), the control flips to
  // "collapse", so ONE click closes everything. Expand-all is offered only
  // from the fully-collapsed state. (Previously it required EVERY folder open
  // before it would collapse, so from a partial-open state the first click
  // falsely re-expanded and you had to click twice.)
  const allFolderIds = useMemo(() => {
    const ids: number[] = []
    const walk = (nodes: FolderNode[]) => {
      for (const n of nodes) { ids.push(n.id); if (n.children) walk(n.children) }
    }
    walk(tree.data || [])
    return ids
  }, [tree.data])
  const anyFolderOpen = allFolderIds.some((id) => openSet.has(id))

  // Clock / alpha selection behaviour (Ken 2026-08-21):
  //   • clock ('affected'): snap the newness window to the tightest band that
  //     still contains activity, so "What's New" never lands on an empty screen
  //     (e.g. a stale 5m border), and collapse the Archived section (parent).
  //   • alpha: expand every folder so the alphabetical listing is fully shown.
  // Snap the clock to the tightest slider stop that still COVERS the newest
  // email (corpus_edge), rounded UP to the next stop so the email is included
  // — a 3.2h-old email lands on the 4h stop, not 3h and not the coarse 12h.
  // Mirrors roundHours' grid: 5-min steps <1h, whole hours <48h, then d/w/mo.
  const tightestWindowWithContent = (): CounterWindow => {
    let newest = 0
    for (const c of Object.values(snapCountersQuery.data ?? {})) {
      const la = (c as MatterCounter | undefined)?.last_activity
      if (la) { const t = Date.parse(la); if (t > newest) newest = t }
    }
    if (!newest) return { preset: '90d' }
    const ah = (Date.now() - newest) / 36e5   // age of newest matter activity, hours
    if (ah <= 0) return windowFromHours(5 / 60)
    let hours: number
    const min = ah * 60
    if (min < 60) hours = Math.max(5, Math.ceil(min / 5) * 5) / 60      // next 5-min
    else if (ah < 48) hours = Math.ceil(ah)                             // next whole hour
    else {
      const d = ah / 24
      if (d < 14) hours = Math.ceil(d) * 24                             // next day
      else if (d < 60) hours = Math.ceil(d / 7) * 24 * 7                // next week
      else hours = Math.ceil(d / 30) * 24 * 30                          // next month
    }
    return windowFromHours(hours)
  }
  const handleSortMode = (m: 'alpha' | 'affected') => {
    markSidebarInteracted()
    if (m === 'affected') {
      saveCounterWindow(tightestWindowWithContent())
      onEnterAffected?.()
    } else {
      setOpenSet(new Set(allFolderIds))
    }
    setSortMode(m)
  }

  // Per-folder DISTINCT-matter recursive counts. Server-side
  // matter_count_recursive sums children's matter_count_self, which
  // double-counts a matter that's in both a parent folder AND one of
  // its sub-folders. This client-side computation builds a
  // Set<matter_id> per folder (self + all descendants), so the count
  // is the size of the union — each matter counted once per subtree
  // regardless of how many sub-folders it appears in.
  //
  // Active for both search and non-search paths so the count chip is
  // always accurate. During search, `matters` is already filtered by
  // Workspace, so the distinct count naturally reflects the filter.
  const folderDistinctMatters = useMemo<Map<number, Set<number>>>(() => {
    const out = new Map<number, Set<number>>()
    const folderMap = new Map<number, FolderNode>()
    const indexNodes = (nodes: FolderNode[]) => {
      for (const n of nodes) {
        folderMap.set(n.id, n)
        if (n.children) indexNodes(n.children)
      }
    }
    indexNodes(tree.data || [])
    for (const m of matters) {
      if (m.status === 'closed') continue
      for (const a of m.folder_assignments || []) {
        let fid: number | null = a.folder
        while (fid != null) {
          let set = out.get(fid)
          if (!set) { set = new Set(); out.set(fid, set) }
          set.add(m.id)
          const parent: number | null | undefined = folderMap.get(fid)?.parent_folder
          fid = parent ?? null
        }
      }
    }
    return out
  }, [matters, tree.data])

  // Per-folder window counters, rolled up over the folder's whole subtree
  // (self + descendants) using folderDistinctMatters, which already unions
  // and dedups matters filed in both a parent and a child. issues = sum of
  // matters' email-issues-added in the window; newMatters = count of matters
  // born in the window. (matterCounters comes from the backend, keyed on the
  // global `since`.)
  const folderCounters = useMemo<Map<number, { issues: number; newMatters: number }>>(() => {
    const out = new Map<number, { issues: number; newMatters: number }>()
    for (const [fid, set] of folderDistinctMatters) {
      let issues = 0, newMatters = 0
      for (const mid of set) {
        const c = matterCounters[String(mid)]
        if (!c) continue
        issues += c.issues
        if (c.new) newMatters += 1
      }
      out.set(fid, { issues, newMatters })
    }
    return out
  }, [folderDistinctMatters, matterCounters])

  // "What's New" (affected) view — matters touched since the border, in folder
  // context. affectedMatterIds = issues>0 in the window (a properly-created
  // matter always carries its founding issue, so issues>0 already covers
  // brand-new ones — Ken 2026-08-17, no separate `new` case). affectedFolderIds
  // = any folder whose subtree rollup has issues>0. Reuses the counters we
  // already fetch every 20s; pure frontend filter over that.
  const affectedMode = sortMode === 'affected'
  const affectedMatterIds = useMemo(() => {
    const s = new Set<number>()
    for (const [mid, c] of Object.entries(matterCounters)) {
      if (c && c.issues > 0) s.add(Number(mid))
    }
    return s
  }, [matterCounters])
  const affectedFolderIds = useMemo(() => {
    const s = new Set<number>()
    for (const [fid, c] of folderCounters) {
      if (c.issues > 0) s.add(fid)
    }
    return s
  }, [folderCounters])

  // Unfiled section auto-opens when search is active AND has unfiled
  // results. User's persisted preference is restored when search clears.
  const displayedUnfiledOpen = searchActive ? true : unfiledOpen

  // Build a Map<folderId, Matter[]> for fast lookup. Each matter appears
  // under each folder it's filed in.
  const mattersByFolder = useMemo(() => {
    const out: Map<number, Matter[]> = new Map()
    const unfiled: Matter[] = []
    for (const m of matters) {
      if (m.status === 'closed') continue  // closed mirror handled separately
      const assignments = m.folder_assignments ?? []
      if (assignments.length === 0) {
        unfiled.push(m)
        continue
      }
      for (const a of assignments) {
        const arr = out.get(a.folder) || []
        arr.push(m)
        out.set(a.folder, arr)
      }
    }
    return { byFolder: out, unfiled }
  }, [matters])

  // Ken 2026-07-05: per-folder "most-recent activity" timestamp
  // (max updated_at across the folder itself, all descendant
  // subfolders, and all matters filed anywhere inside it). Powers
  // the 'recent' sort mode: folders bubble up when a matter deep
  // inside them just got touched. Computed once from tree + matters
  // and passed down through FolderRow.
  const folderRecent = useMemo(() => {
    const out = new Map<number, number>()  // folderId → ms timestamp
    if (!tree.data) return out
    // Direct matters per folder (already includes descendants via
    // folderDistinctMatters), so we can just look up each folder's
    // set and take max updated_at.
    const matterById = new Map(matters.map((m) => [m.id, m]))
    const walk = (node: FolderNode): number => {
      let best = node.updated_at ? Date.parse(node.updated_at) : 0
      const matterSet = folderDistinctMatters.get(node.id)
      if (matterSet) {
        for (const mid of matterSet) {
          const m = matterById.get(mid)
          if (m && m.updated_at) {
            const t = Date.parse(m.updated_at)
            if (t > best) best = t
          }
        }
      }
      for (const c of node.children || []) {
        const t = walk(c)
        if (t > best) best = t
      }
      out.set(node.id, best)
      return best
    }
    for (const root of tree.data) walk(root)
    return out
  }, [tree.data, matters, folderDistinctMatters])

  // Auto-expand folders containing the selected matter (so refresh / deep-link
  // lands on a visible row). Only fires when selectedMatterId or selectedFolderId
  // changes — won't override user's manual collapses on idle.
  useEffect(() => {
    if (selectedFolderId == null || tree.data == null) return
    // On a hard refresh (no interaction yet) don't auto-open the selected
    // matter's folder chain — the sidebar stays fully collapsed. Once the user
    // has clicked something this session, selecting a matter reveals it as
    // before (Ken 2026-08-19).
    if (!sidebarUserInteracted()) return
    setOpenSet((prev) => {
      const next = new Set(prev)
      // Walk up from selectedFolderId to root and open every ancestor.
      const allFolders = flattenTree(tree.data!)
      let current = allFolders.find((f) => f.id === selectedFolderId)
      while (current) {
        next.add(current.id)
        const parent = allFolders.find((f) => f.id === current!.parent_folder)
        current = parent
      }
      return next
    })
  }, [selectedFolderId, tree.data])

  const create = useMutation({
    mutationFn: (payload: { name: string; parent_folder?: number | null; description?: string }) =>
      foldersApi.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folders', 'tree'] }),
  })

  const [newTopOpen, setNewTopOpen] = useState(false)

  // Measure the pinned controls row so the sticky top-level folder headers
  // park directly beneath it (Emerson 2026-08-31). ResizeObserver keeps the
  // offset correct across font-size / zoom / theme changes; the 28px fallback
  // covers the first paint and the (test/SSR) no-ResizeObserver case. Exposed
  // to the tree as the --me-controls-h CSS var, read by FolderRow headers.
  const controlsRef = useRef<HTMLDivElement>(null)
  const [controlsH, setControlsH] = useState(28)
  useEffect(() => {
    const el = controlsRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const measure = () => setControlsH(el.offsetHeight)
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    measure()
    return () => ro.disconnect()
  }, [])

  // Distinct count across all folders + unfiled. Sum of root recursive
  // counts would double-count matters filed in multiple top-level
  // folders, so we union all folder sets at the root level.
  // IMPORTANT: this useMemo must run on every render BEFORE any early
  // returns below — Rules of Hooks. Compute roots inline here.
  const roots = tree.data || []
  // (SYNCED moved to the Workspace pane-1 header as the top stamp — the corpus-edge
  // query lives there now, not here. SPEC rev 2026-10-25.)

  if (tree.isLoading) {
    return <div className="px-2 py-3 text-[11px] me-text-mute">Loading folders…</div>
  }
  if (tree.isError) {
    return <div className="px-2 py-3 text-[11px] me-text-mute">Couldn't load folders.</div>
  }

  // The virtual Unfiled/Personal bucket. Real tenants pin it at the top; playhead
  // tenants (KEN) slot it ALPHABETICALLY among the folders (Ken 2026-10-21 — so
  // "Personal" lands between Litigation and Policies). Shared element factory so
  // both placements stay identical.
  const showUnfiled = affectedMode
    ? mattersByFolder.unfiled.some((m) => affectedMatterIds.has(m.id))
    : mattersByFolder.unfiled.length > 0
  const renderUnfiled = () => (
    <UnfiledSection
      key="__unfiled"
      matters={mattersByFolder.unfiled}
      selectedMatterId={selectedMatterId}
      selectedFolderId={selectedFolderId}
      onSelectMatter={onSelectMatter}
      onArchiveMatter={onArchiveMatter}
      isOpen={displayedUnfiledOpen}
      onToggle={() => setUnfiledOpen((v) => !v)}
      seenAtLoad={seenAtLoad}
      markSeen={markSeen}
      matterCounters={matterCounters}
      boldMatter={boldMatter}
      searchActive={searchActive}
      affectedMode={affectedMode}
      affectedMatterIds={affectedMatterIds}
    />
  )

  // KEN pane-1 head register values. "WHAT'S NEW" = the current counter window.
  const _kenWinH = windowToHours(counterWindow)
  const kenWindowLabel = _kenWinH == null
    ? 'All time'
    : _kenWinH < 48 ? `Last ${Math.round(_kenWinH)} hours` : `Last ${Math.round(_kenWinH / 24)} days`
  // Register label style (SPEC rev 2026-10-25): ui-monospace 10px / .11em / #9B958A
  // uppercase in a 100px column (reverts the brief SPEC-0004 M-### label style).
  // Layout only — the type comes from the .me-chrome-label token (Ken 2026-10-25).
  const kenLabelCol = { width: 100, flexShrink: 0 } as const

  return (
    <div className="space-y-0.5" style={{ ['--me-controls-h' as any]: `${controlsH}px` }}>
      {/* Secretary chat host — mounted once; pane-1 name clicks open it (KEN only). */}
      <SecretaryChatHost />
      {/* Header row of the folder tree (Ken 2026-08-10 mockup):
            423 matters            [↓a ⏱ #]      [ new in last 7d ⌄ ]
          — matter counter pinned LEFT as a static, isolated zone (the one
          number the picker doesn't touch). On the RIGHT, grouped: collapse-all
          caret, the sort-mode icons, and the window picker. The picker names
          the column it controls: "new in last 7d" (rolling window) vs
          "new since Aug 2" (fixed border) — the preposition tells you which.
          "+ new folder" is hover-only with no reserved slot. */}
      {/* Pinned: this controls row sticks to the top of the scroll body so the
          matter counter + sort + picker stay visible while the tree scrolls
          under it (Ken 2026-08-10). The border-b is "the thin grey line". */}
      {playhead ? (
        /* KEN pane-1 head: the WHAT'S "NEW" window row + slider (SYNCED lives at the
           top of the pane header), then the category list (no rules). Labels
           match the pane-2 M-### style (JetBrains Mono / 11px / muted / uppercase).
           Replaces the old count / expand-caret / segmented-sort / window-dial strip.
           MATTER ORDER (A–Z / Recent) was removed 2026-10-25 — unused. */
        <div ref={controlsRef} className="sticky top-0 z-10 bg-[color:var(--me-surface)]" style={{ paddingTop: 8 }}>
          {/* WHAT'S NEW register row + its slider, then the category list (no rule). */}
          <div className="flex items-baseline" style={{ padding: '5px 0' }}>
            <span className="me-chrome-label" style={kenLabelCol}>WHAT&apos;S NEW</span>
            <span className="me-chrome-value">{kenWindowLabel}</span>
          </div>
          <div style={{ paddingLeft: 100, paddingTop: 2, paddingBottom: 2 }}>
            <KenWindowSlider window={counterWindow} onChange={saveCounterWindow} />
          </div>
          {/* NO horizontal rules in the KEN pane-1 head — spacing alone separates
              the WHAT'S NEW window from the category list; the first category sits
              14px below the slider (Ken 2026-10-25). MATTER ORDER (A–Z / Recent)
              removed same day — unused. */}
          <div style={{ height: 14 }} />
        </div>
      ) : (
      <div ref={controlsRef} className="sticky top-0 z-10 bg-[color:var(--me-surface)] relative flex items-center justify-between border-b me-border pb-1.5 mb-1 group">
        {/* LEFT — static matter total, its own zone. "+ new folder" is a
            hover-only affordance anchored just right of the count; it's
            absolutely positioned so it reserves no layout slot. */}
        <div className="relative">
          {/* Playhead (KEN): the count duplicates the search placeholder
              ("Find N matters"), so it's dropped here (Ken 2026-10-23). */}
          {!playhead && (
            <span
              className="me-brand normal-case font-normal tabular-nums text-[11px] me-text"
              title="Active matters"
            >
              {(matterCount ?? matters.length).toLocaleString()} matters
            </span>
          )}
          {/* "New top-level folder" affordance removed — not needed, and the
              hover-reveal was noisy (Ken 2026-08-19). */}
        </div>
        {/* RIGHT cluster — caret, sort, picker. */}
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={() => { traceSort(anyFolderOpen ? 'collapse' : 'expand'); setOpenSet(anyFolderOpen ? new Set() : new Set(allFolderIds)) }}
            className="me-text-mute hover:me-text inline-flex items-center justify-center h-[16px] w-[18px]"
            title={anyFolderOpen ? 'Collapse all folders' : 'Expand all folders'}
            aria-label={anyFolderOpen ? 'Collapse all folders' : 'Expand all folders'}
            aria-expanded={anyFolderOpen}
          >
            <ChevronRight size={12} className={`transition-transform ${anyFolderOpen ? 'rotate-90' : ''}`} />
          </button>
          <SortModeToggle mode={sortMode} onChange={handleSortMode} searchActive={searchActive} onClearSearch={onClearSearch} />
          <WindowDial window={counterWindow} onChange={saveCounterWindow} onGrab={() => { if (!playhead) { setSortMode('affected'); onEnterAffected?.() } }} />
        </div>
      </div>
      )}

      {newTopOpen && (
        <NewFolderInline
          parentId={null}
          onSubmit={(name) => { create.mutate({ name }); setNewTopOpen(false) }}
          onCancel={() => setNewTopOpen(false)}
        />
      )}

      {/* Unfiled pinned to the very top (real tenants — surfaces unsorted
          matters before any organized folders). Playhead tenants (KEN) instead
          slot it alphabetically among the folders below (Ken 2026-10-21). */}
      {!playhead && showUnfiled && renderUnfiled()}

      {/* "What's New" empty state — nothing crossed the border in this window. */}
      {affectedMode && affectedMatterIds.size === 0 && (
        <div className="px-2 py-6 text-center text-[11px] me-text-mute italic">
          Nothing new in this window.
        </div>
      )}

      {(() => {
        const visibleRoots = affectedMode ? roots.filter((r) => affectedFolderIds.has(r.id)) : roots
        const folderEls = visibleRoots.map((root) => (
          <FolderRow
            key={root.id}
            node={root}
            openSet={displayedOpenSet}
            onToggle={toggle}
            mattersByFolder={mattersByFolder.byFolder}
            selectedMatterId={selectedMatterId}
            selectedFolderId={selectedFolderId}
            onSelectMatter={onSelectMatter}
            onArchiveMatter={onArchiveMatter}
            isTop
            searchActive={searchActive}
            folderDistinctMatters={folderDistinctMatters}
            folderCounters={folderCounters}
            matterCounters={matterCounters}
            boldFolder={boldFolder}
            boldSubfolder={boldSubfolder}
            boldMatter={boldMatter}
            seenAtLoad={seenAtLoad}
            markSeen={markSeen}
            folderRecent={folderRecent}
            affectedMode={affectedMode}
            affectedMatterIds={affectedMatterIds}
            affectedFolderIds={affectedFolderIds}
          />
        ))
        // Playhead tenants (KEN): drop the Unfiled/Personal bucket into its
        // alphabetical position among the folders rather than pinning it on top.
        if (playhead && showUnfiled) {
          let idx = visibleRoots.findIndex(
            (r) => unfiledLabel.localeCompare(r.name, undefined, { sensitivity: 'base' }) < 0)
          if (idx < 0) idx = folderEls.length
          folderEls.splice(idx, 0, renderUnfiled())
        }
        return folderEls
      })()}
    </div>
  )
}

/** Ken 2026-07-05: segmented two-option control for the sidebar
 *  folder-contents sort. Radio semantics (one always active).
 *  Rounded-rect container, two icon buttons side by side. Active =
 *  filled + inverted; inactive = ghost. Icons + colour match the
 *  neighbouring "+ new" button (size 10, me-text-mute base). No
 *  persistence per Ken — resets to 'alpha' each mount. Placement
 *  is inline in the "FOLDERS: N MATTERS" row to the left of + new. */
function SortModeToggle({
  mode, onChange, searchActive, onClearSearch,
}: { mode: 'alpha' | 'affected'; onChange: (m: 'alpha' | 'affected') => void; searchActive?: boolean; onClearSearch?: () => void }) {
  const btn = (kind: 'alpha' | 'affected', Icon: typeof ArrowDownAZ, title: string) => {
    const active = mode === kind
    return (
      <button
        type="button"
        role="radio"
        aria-checked={active}
        // "What's New" (affected) toggles OFF back to alpha when re-clicked
        // (Ken 2026-08-17) — a view you can dismiss without picking another.
        // Clock while a search is active: also clear the search bar so entering
        // 'What's New' sticks, instead of the auto-relax bouncing back to alpha
        // (Ken 2026-08-18).
        onClick={() => { traceSort(kind === 'affected' ? 'clock' : 'alpha'); if (kind === 'affected' && searchActive) onClearSearch?.(); onChange(active && kind === 'affected' ? 'alpha' : kind) }}
        title={title}
        // Active background = a neutral grey (--me-selected), Ken 2026-08-10.
        // (The brand/nav colour moved off the sort toggle to the search bar.)
        className={
          'inline-flex items-center justify-center rounded-sm h-[16px] w-[18px] transition-colors ' +
          (active
            ? 'bg-[color:var(--me-selected)] me-text'
            : 'me-text-mute hover:me-text')
        }
      >
        <Glyph name={kind === 'alpha' ? 'sort_alpha' : 'sort_clock'} size={10}><Icon size={10} /></Glyph>
      </button>
    )
  }
  return (
    <div
      role="radiogroup"
      className="inline-flex items-center gap-0.5 rounded border me-border p-0.5"
    >
      {btn('alpha', ArrowDownAZ, 'Alphabetically within folders')}
      {btn('affected', Clock, 'New activity only')}
    </div>
  )
}

function flattenTree(roots: FolderNode[]): FolderNode[] {
  const out: FolderNode[] = []
  function walk(nodes: FolderNode[]) {
    for (const n of nodes) {
      out.push(n)
      if (n.children?.length) walk(n.children)
    }
  }
  walk(roots)
  return out
}

function UnfiledSection({
  matters, selectedMatterId, selectedFolderId, onSelectMatter, onArchiveMatter,
  isOpen, onToggle, seenAtLoad, markSeen, matterCounters, boldMatter, searchActive,
  affectedMode, affectedMatterIds,
}: {
  matters: Matter[]
  selectedMatterId: number | 'inbox' | null
  selectedFolderId: number | null
  onSelectMatter: (matterId: number, folderId: number | null) => void
  onArchiveMatter?: (matterId: number) => void
  isOpen: boolean
  onToggle: () => void
  seenAtLoad?: Set<number> | null
  markSeen?: (id: number) => void
  matterCounters?: Record<string, MatterCounter>
  boldMatter?: boolean
  searchActive?: boolean
  affectedMode?: boolean
  affectedMatterIds?: Set<number>
}) {
  // Per-tenant label for this bucket ("Unfiled" by default; a demo tenant may
  // show "Personal"). (Ken 2026-10-21.)
  const { unfiledLabel } = useProductName()
  const uauth = useAuth()
  const uPlayhead = uauth.status === 'authenticated' && isPlayheadTenant(uauth.user.client?.code)
  // In 'affected' (What's New) mode: show only matters touched since the border,
  // and order by when they were last affected (counters.last_activity).
  const shownMatters = affectedMode
    ? matters.filter((m) => affectedMatterIds?.has(m.id))
    : matters
  const lastActivityMs = (m: Matter) => {
    const la = matterCounters?.[String(m.id)]?.last_activity
    return la ? Date.parse(la) : (m.updated_at ? Date.parse(m.updated_at) : 0)
  }
  const sorted = [...shownMatters].sort((a, b) =>
    affectedMode
      ? lastActivityMs(b) - lastActivityMs(a) || a.name.localeCompare(b.name)
      : a.name.localeCompare(b.name),
  )
  const displayOpen = affectedMode ? true : isOpen
  // Outer-div + inner-button structure mirrors FolderRow exactly so the
  // chevron's left edge aligns with top-level folders. A direct
  // <button> wrapper renders the chevron with a slight horizontal
  // offset on some browsers (button display + flex interaction).
  return (
    <div className="mb-0.5">
      <div
        className="group w-full flex items-center justify-between pl-0 pr-1 py-1 rounded-md hover:bg-[color:var(--me-hover)]"
        title={`${sorted.length} matter${sorted.length === 1 ? '' : 's'} not in any folder`}
      >
        <button
          onClick={onToggle}
          className={`flex items-center flex-1 min-w-0 ${uPlayhead ? '' : 'gap-1'}`}
          style={uPlayhead ? { paddingLeft: PH_BASE } : undefined}
        >
          {uPlayhead ? (
            // Top-level bucket (depth 0): byte-for-byte the same treatment as a normal
            // top-level category (UXT-5 5B) — same paddingLeft, same stock lucide Folder
            // (12px #78716c; the 'folder' glyph slot is suppressed), same label style.
            <>
              <span className="shrink-0 inline-flex items-center justify-center"
                    style={{ width: PH_GLYPH, height: PH_GLYPH, marginRight: PH_GAP, color: '#78716c' }}>
                <FolderIcon size={12} />
              </span>
              <span
                className="me-folder-name truncate inline-block uppercase"
                style={bandPillStyle()}
              >{unfiledLabel}</span>
            </>
          ) : (
            <>
              <ChevronRight size={11} className={`transition-transform ${displayOpen ? 'rotate-90' : ''} me-text-mute shrink-0`} />
              <Glyph name="folder" size={15} className="me-text-mute shrink-0 mr-1"><FolderIcon size={15} className="me-text-mute shrink-0 mr-1" /></Glyph>
              <span className="font-medium me-text text-[15px]">{unfiledLabel}</span>
            </>
          )}
        </button>
        {/* Plain grey count (no shaded chip) to match the new folder-row
            number style (Ken 2026-08-11). Playhead: use the SAME reserved gutter
            as folder/matter rows (empty 30px Manage slot + fixed 22px count column)
            so Personal's number lines up with every other level on the right edge
            — the previous bare span carried an extra pr-1 and sat 4px left of the
            rest (Ken 2026-10-22). */}
        {uPlayhead ? (
          <div className="shrink-0 flex items-center justify-end pointer-events-none" style={{ height: 26 }}>
            {/* Personal is a top-level category too (Ken 2026-10-23): same 30px
                Manage slot, but as a render-function (no hook state) its ⋯ opens
                the Secretary chat directly rather than a one-item dropdown. */}
            <div className="shrink-0 flex items-center justify-center" style={{ width: 30 }}>
              <button
                onClick={(e) => { e.stopPropagation(); openSecretaryChat({ type: 'unfiled' }, unfiledLabel) }}
                className="pointer-events-auto inline-flex items-center justify-center rounded me-text-mute hover:me-text opacity-0 group-hover:opacity-100 transition-opacity duration-[120ms]"
                style={{ width: 26, height: 26 }}
                title={`Ask about ${unfiledLabel}`}
                aria-label={`Ask about ${unfiledLabel}`}
              >
                <Glyph name="manage" size={15}><MoreHorizontal size={15} /></Glyph>
              </button>
            </div>
            {/* No count on Personal (Ken 2026-10-25): Personal items are never
                portrayed as "new". Empty 22px column keeps the ⋯ aligned. */}
            <div className="shrink-0" style={{ width: 22 }} />
          </div>
        ) : (
          <span className="me-mono text-[11px] tabular-nums shrink-0 pr-1" style={{ color: 'var(--me-count-color, #8E8E8E)' }}>{sorted.length.toLocaleString()}</span>
        )}
      </div>
      {displayOpen && (
        <div className={`mt-0.5 ${uPlayhead ? '' : 'pl-3'}`}>
          {sorted.map((m) => (
            <MatterRowCompact
              key={m.id}
              matter={m}
              level={1}
              selected={selectedMatterId === m.id && selectedFolderId === null}
              onSelect={() => onSelectMatter(m.id, null)}
              onArchive={onArchiveMatter ? () => onArchiveMatter(m.id) : undefined}
              isNew={seenAtLoad != null && !seenAtLoad.has(m.id)}
              markSeen={markSeen}
              counter={matterCounters?.[String(m.id)]}
              boldName={boldMatter}
              searchActive={searchActive}
              showActivityAgo={affectedMode}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Folder-header label for the window picker (Ken 2026-08-11): just the VALUE
// on the face — "30d" / "Jul 6" / "all time" — no surrounding words.
const WINDOW_VALUE: Record<Exclude<WindowPreset, 'all' | 'custom' | 'rel'>, string> = {
  '2h': '2h', '12h': '12h', '24h': '24h', '7d': '7d', '30d': '30d', '90d': '90d',
}
export function newCountLabel(w: CounterWindow): string {
  if (w.preset === 'custom' && w.at) {
    const d = new Date(w.at)
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
  }
  if (w.preset === 'all') return 'all time'
  if (w.preset === 'rel') return relLabel(w.hours || 24)
  return WINDOW_VALUE[w.preset as Exclude<WindowPreset, 'all' | 'custom' | 'rel'>] ?? '7d'
}

// ── Newness-border DIAL (A2, Ken 2026-08-17) ───────────────────────────────
// A log-scaled drag-track that REPLACES the preset dropdown. Right end = last
// hour, left end = all time; drag or tap to sweep the border continuously (the
// /counters endpoint takes float hours, so any value works). Live label +
// faint reference ticks at 1d / 1w / 1m give spatial muscle memory, no menu.
const DIAL_MIN_H = 5 / 60             // right end = last 5 minutes
const DIAL_MAX_H = 24 * 90            // left end  = last 3 months
const DIAL_LN_MIN = Math.log(DIAL_MIN_H)
// DEMO playhead: hard-cap the far (left) edge at 7 days — the story origin —
// so the playhead can't be dragged back past where the demo begins (Ken
// 2026-08-24). Real tenants keep the full 3-month reach.
const DIAL_DEMO_MAX_H = 24 * 7
// track fraction (0 = left/maxH, 1 = right/5m) → hours back. `maxH` sets the
// left-edge reach (default 3mo; the demo passes 7d to lock it there).
function dialFToHours(f: number, maxH = DIAL_MAX_H): number {
  const lnMax = Math.log(maxH)
  return Math.exp(lnMax - f * (lnMax - DIAL_LN_MIN))
}
// hours back → track fraction (null = a legacy 'all' window ⇒ far left)
function dialHoursToF(h: number | null, maxH = DIAL_MAX_H): number {
  if (h === null) return 0
  const lnMax = Math.log(maxH)
  const clamped = Math.min(maxH, Math.max(DIAL_MIN_H, h))
  return (lnMax - Math.log(clamped)) / (lnMax - DIAL_LN_MIN)
}
// KEN pane-1 head slider (SPEC-0003): the "NEW" WINDOW control, restyled to the
// spec — 148px track, 3px #E3DED4, fill #BFA182, 9px square #A4522F handle (r2).
// Reuses the shared window state + dial math (committed hours ⇄ fraction).
function KenWindowSlider({ window: w, onChange }: { window: CounterWindow; onChange: (win: CounterWindow) => void }) {
  const trackRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)
  const TRACK = 140, HANDLE = 9
  const committedH = windowToHours(w)
  const f = dialHoursToF(committedH, DIAL_DEMO_MAX_H)
  const left = Math.round(f * (TRACK - HANDLE))
  const hoursAtX = (clientX: number): number => {
    const el = trackRef.current
    if (!el) return committedH ?? 24
    const r = el.getBoundingClientRect()
    const frac = Math.min(1, Math.max(0, (clientX - r.left) / r.width))
    return roundHours(dialFToHours(frac, DIAL_DEMO_MAX_H))
  }
  const onDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    draggingRef.current = true
    e.currentTarget.setPointerCapture?.(e.pointerId)
    onChange(windowFromHours(hoursAtX(e.clientX)))
  }
  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    onChange(windowFromHours(hoursAtX(e.clientX)))
  }
  const onUp = () => { draggingRef.current = false }
  return (
    <div
      ref={trackRef}
      onPointerDown={onDown}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerCancel={onUp}
      className="relative cursor-pointer"
      style={{ width: TRACK, height: 12, touchAction: 'none' }}
    >
      <div className="absolute left-0 top-1/2 -translate-y-1/2" style={{ width: TRACK, height: 3, background: '#E3DED4', borderRadius: 2 }} />
      <div className="absolute left-0 top-1/2 -translate-y-1/2" style={{ width: left + HANDLE / 2, height: 3, background: '#BFA182', borderRadius: 2 }} />
      <div className="absolute top-1/2 -translate-y-1/2" style={{ left, width: HANDLE, height: HANDLE, background: '#A4522F', borderRadius: 2 }} />
    </div>
  )
}

function WindowDial({ window: w, onChange, onGrab }: { window: CounterWindow; onChange: (win: CounterWindow) => void; onGrab?: () => void }) {
  const trackRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)
  const lastCommit = useRef(0)
  const [dragH, setDragH] = useState<number | undefined>(undefined) // undefined ⇒ not dragging

  // DEMO tenant (Ken 2026-08-24): the dial is a STORY PLAYHEAD, not a lookback
  // window — its position is a past moment we've rewound to (asof = now − value),
  // and the right edge is "now". So relabel it as a timeline: "7d ago" … "now"
  // (a near-present position reads as "now"), and hard-cap the far edge at 7d
  // (the story origin). Real tenants are untouched.
  const auth = useAuth()
  const playhead = auth.status === 'authenticated'
    && isPlayheadTenant(auth.user.client?.code)
  const maxH = playhead ? DIAL_DEMO_MAX_H : DIAL_MAX_H

  const committedH = windowToHours(w)
  const rawShownH = dragH === undefined ? committedH : dragH
  // Clamp a stale stored value into the demo's 7d reach so the thumb + label
  // never read past the locked edge.
  const shownH = playhead && rawShownH != null ? Math.min(rawShownH, maxH) : rawShownH
  const f = dialHoursToF(shownH, maxH)
  const atNow = shownH === null || (shownH !== null && shownH < 0.4)  // < ~24min ⇒ present
  const label = playhead
    ? (atNow ? 'now' : `${relLabel(shownH)} ago`)
    : (shownH === null ? 'all' : relLabel(shownH))
  const dialTitle = playhead
    ? (atNow ? 'Story playhead — now (present day)' : `Story playhead — the matter as of ${relLabel(shownH)} ago`)
    : (label === 'all' ? 'Newness border: all time' : `Newness border: last ${label}`)

  const hoursAtX = (clientX: number): number => {
    const el = trackRef.current
    if (!el) return committedH ?? 24
    const r = el.getBoundingClientRect()
    const frac = Math.min(1, Math.max(0, (clientX - r.left) / r.width))
    return roundHours(dialFToHours(frac, maxH))
  }
  // Thumb + label update instantly from local state; the counters refetch is
  // throttled during the drag (and always committed on release).
  const commit = (h: number | null, force = false) => {
    const now = Date.now()
    if (!force && now - lastCommit.current < 180) return
    lastCommit.current = now
    onChange(windowFromHours(h))
  }
  const onDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    draggingRef.current = true
    onGrab?.()  // grabbing the border dial drops you straight into "What's New"
    e.currentTarget.setPointerCapture?.(e.pointerId)
    const h = hoursAtX(e.clientX)
    setDragH(h); commit(h, true)
  }
  const onMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    const h = hoursAtX(e.clientX)
    setDragH(h); commit(h)
  }
  const onUp = () => {
    if (!draggingRef.current) return
    draggingRef.current = false
    if (dragH !== undefined) commit(dragH, true)
    traceSort('dial', label)
    setDragH(undefined)
  }
  return (
    <div
      className="flex items-center gap-1 select-none"
      title={dialTitle}
    >
      <span className={`me-brand text-[10px] normal-case font-normal me-text tabular-nums ${playhead ? 'w-12' : 'w-6'} text-right`}>{label}</span>
      <div
        ref={trackRef}
        role="slider"
        aria-label={playhead ? 'Story playhead' : 'Newness border'}
        aria-valuetext={playhead ? label : (label === 'all' ? 'all time' : `last ${label}`)}
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
        className="relative h-3.5 w-14 flex items-center cursor-ew-resize touch-none"
      >
        <div className="absolute left-0 right-0 h-[3px] rounded-full bg-[color:var(--me-border)]" />
        {/* covered span: from the thumb (border) to now (right edge) */}
        <div className="absolute h-[3px] rounded-full" style={{ left: `${f * 100}%`, right: 0, background: playhead ? '#776E5E' : 'var(--me-nav-color)' }} />
        {/* faint landmarks — real: 1h/1d/1w/1m; demo playhead: 1d/3d/6d (all
            inside the 7d-locked track). */}
        {(playhead ? [24, 24 * 3, 24 * 6] : [1, 24, 168, 720]).map((t) => (
          <div key={t} className="absolute w-px h-1.5" style={{ left: `${dialHoursToF(t, maxH) * 100}%`, background: playhead ? '#d6d3d1' : '#8E8E8E', opacity: 0.55 }} />
        ))}
        <div
          className="absolute rounded-full"
          style={{ left: `${f * 100}%`, width: playhead ? 11 : 9, height: playhead ? 11 : 9, transform: 'translateX(-50%)', background: playhead ? '#57534e' : 'var(--me-nav-color)', boxShadow: playhead ? 'none' : '0 0 0 1px var(--me-surface)' }}
        />
      </div>
    </div>
  )
}

// Global time-window picker for the activity counters. One setting, stored in
// localStorage (me_counter_window), applied to every folder + matter counter.
// `prominent` renders the larger pane-1 variant (unused now — kept for reuse).
// `renderLabel` overrides the button text (folder header uses newCountLabel).
export function WindowControl({
  window: w, onChange, prominent = false, renderLabel,
}: { window: CounterWindow; onChange: (w: CounterWindow) => void; prominent?: boolean; renderLabel?: (w: CounterWindow) => string }) {
  const [open, setOpen] = useState(false)
  const presets: { key: WindowPreset; label: string }[] = [
    { key: '2h', label: 'Last 2 hours' },
    { key: '12h', label: 'Last 12 hours' },
    { key: '24h', label: 'Last 24 hours' },
    { key: '7d', label: 'Last 7 days' },
    { key: '30d', label: 'Last 30 days' },
    { key: '90d', label: 'Last 90 days' },
    { key: 'all', label: 'All time' },
  ]
  return (
    <div className={prominent ? 'relative inline-block' : 'relative'}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={
          prominent
            ? 'me-brand inline-flex items-center gap-1 rounded-md border me-border bg-[color:var(--me-surface)] px-2.5 py-1 text-[12px] normal-case font-normal me-text hover:bg-[color:var(--me-hover)]'
            : 'me-brand inline-flex items-center rounded border me-border px-1.5 py-0.5 text-[10px] normal-case font-normal me-text-mute hover:me-text'
        }
      >
        {renderLabel ? renderLabel(w) : windowLabel(w)}
        <ChevronRight size={prominent ? 12 : 10} className={`transition-transform rotate-90 ${prominent ? '' : 'ml-0.5'}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[900]" onClick={() => setOpen(false)} />
          {/* Anchored to the controller's top-RIGHT so it juts LEFT, not right
              into pane-2 where it was getting hidden (Ken 2026-08-11). */}
          <div className="absolute right-0 z-[901] mt-1 w-44 rounded-md border me-border bg-[color:var(--me-surface)] p-1 text-[11px] normal-case font-normal shadow-lg" role="menu">
            <div className="px-2 pt-0.5 pb-1 text-[10px] font-semibold uppercase tracking-wider me-text-mute">Newness Border</div>
            {presets.map((p) => (
              <button
                key={p.key}
                role="menuitemradio"
                aria-checked={w.preset === p.key}
                onClick={() => { onChange({ preset: p.key }); setOpen(false) }}
                className={`block w-full rounded px-2 py-1 text-left hover:bg-[color:var(--me-hover)] ${w.preset === p.key ? 'me-text font-medium' : 'me-text-dim'}`}
              >
                {p.label}
              </button>
            ))}
            <div className="mt-1 border-t me-border pt-1">
              <label className="block px-2 py-0.5 text-[10px] me-text-mute">Since a specific time</label>
              <input
                type="datetime-local"
                value={w.preset === 'custom' ? (w.at || '') : ''}
                onChange={(e) => { if (e.target.value) { onChange({ preset: 'custom', at: e.target.value }); setOpen(false) } }}
                className="me-input !text-[11px] !py-1 mx-1 mb-0.5"
                style={{ width: 'calc(100% - 8px)' }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}

// One count number in a fixed-width, right-aligned SUB-SLOT, so the two
// numbers on a row always sit in the same two columns — position carries the
// meaning (no per-row labels). 11px mono, tabular numerals. Colour is
// INHERITED from the layer rendering it (#8E8E8E at rest, #14110E on
// hover/focus). Blank when 0 so a silent column shows nothing yet still holds
// its place. Capped at 999+.
function CounterNum({ value, tight, ken }: { value: number; tight?: boolean; ken?: boolean }) {
  // `tight` drops the fixed 28px right-aligned column so the digits shrink to
  // content width — used on the matter row so the new-matter pip can hug the
  // number instead of sitting a column-width away (Ken 2026-08-12).
  // `ken` pips ride the chrome/label token (same as SYNCED). MOVU keeps system sans.
  return (
    <span
      className={`inline-block leading-none tabular-nums ${ken ? 'me-chrome-label me-pip' : 'me-brand text-[11px]'} ${tight ? '' : 'w-7 text-right'}`}
    >
      {value > 0 ? (value > 999 ? '999+' : value) : ''}
    </span>
  )
}

// ── ROW ACTION SLOT (Ken 2026-08-10) ──────────────────────────────────────
// Fixed-width, right-aligned slot on EVERY row, in every state. At rest it
// shows numbers; on reveal (dwell / slot-zone / keyboard focus) the numbers
// hide and the text actions appear IN THE SAME SLOT — never both at once. The
// box never resizes (only opacity transitions), so the row name (flex-1,
// truncate) never reflows. Width fits the wider of {numbers, actions}.
const ROW_SLOT_W = 'w-[88px]'

// Playhead (KEN) pane-1 grid (Ken 2026-10-22): no disclosure carets; the glyph
// column carries the indent on a single grid. Glyph box 16, gap 16→text 8, each
// depth steps the glyph column by 16 from a fixed base. Real tenants (MOVU) keep
// the caret + accumulated-padding layout untouched.
const PH_BASE = 8
const PH_STEP = 16
const PH_GLYPH = 16
const PH_GAP = 8
function FolderCountSlot({
  searchActive, revealed, matchCount, issues, newMatters, tools, persistentCount,
}: {
  searchActive: boolean
  revealed: boolean
  matchCount: number
  issues: number
  newMatters: number
  tools: ReactNode  // text actions (New / Rename)
  /** Playhead (KEN): a matter count shown on EVERY collapsible row, always
   *  (the carets are gone, so this is what distinguishes a collapsed folder
   *  from an empty one). Same muted treatment as the matter-row number. */
  persistentCount?: number
}) {
  const show = revealed ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
  // ONE number now: email-issues added in the window (Ken 2026-08-11). The
  // new-matter count is gone — a matter's newness is a per-matter pip on its
  // row instead. Aligns with the single matter-row number, same right column.
  void newMatters
  // Folder rollup count — a quiet grey "peek" at new activity, shown ONLY when
  // the folder is COLLAPSED (see the cIssues gate in FolderRow). Not bold: with
  // the matters hidden there's nothing adjacent to distinguish it from, and the
  // What's New view is the loud activity signal (Ken 2026-08-18).
  const numsEl = issues > 0 ? (
    <div className="flex items-center justify-end" style={{ color: 'var(--me-count-color, #8E8E8E)' }}>
      <CounterNum value={issues} />
    </div>
  ) : null
  // While searching, the slot shows the per-folder MATCH count (how many of the
  // folder's matters match the query). Formatted like the issue count — plain
  // grey number, no boxed me-chip — and hidden at 0, since a no-match folder is
  // already dimmed to opacity-50 (Ken 2026-08-12; was a bunch of "0" boxes).
  const matchEl = matchCount > 0 ? (
    <div className="flex items-center justify-end" style={{ color: 'var(--me-count-color, #8E8E8E)' }}>
      <CounterNum value={matchCount} />
    </div>
  ) : null
  // Playhead: always show the folder's matter count (muted), regardless of
  // open/closed or search — it replaces the caret as the empty-vs-collapsed tell.
  const persistentEl = persistentCount != null ? (
    <div className="flex items-center justify-end" style={{ color: 'var(--me-count-color, #8E8E8E)' }}>
      <CounterNum value={persistentCount} />
    </div>
  ) : null
  const restEl = persistentCount != null ? persistentEl : (searchActive ? matchEl : numsEl)
  // Numbers stay pinned to the RIGHT and visible at all times; the dwell tools
  // reveal to their LEFT so they never cover the count (Ken 2026-08-12). The
  // tools keep their layout width at rest (only opacity animates) so the count
  // never reflows. Text size is set on the tools container — Tailwind v4's
  // layered utilities lose to the unlayered `button{font-size:inherit}` reset
  // if put on a <button> directly.
  return (
    <div className={`relative shrink-0 h-[20px] ${ROW_SLOT_W}`}>
      <div className="absolute inset-0 flex items-center justify-end gap-2">
        <div className={`flex items-center gap-2 text-[11px] transition-opacity duration-[120ms] ${show}`}>
          {tools}
        </div>
        <div className="flex shrink-0 items-center justify-end gap-[4px]">
          {restEl}
          {/* Reserve the SAME 6px column the matter row keeps for its new-pip,
              so a folder's number right-aligns with the matter numbers below it
              — not with the pip column (Ken 2026-08-17 alignment fix). */}
          <span className="shrink-0 w-[6px]" aria-hidden="true" />
        </div>
      </div>
    </div>
  )
}

function FolderRow({
  node, openSet, onToggle, mattersByFolder, selectedMatterId, selectedFolderId, onSelectMatter, onArchiveMatter, isTop,
  searchActive, folderDistinctMatters, folderCounters, matterCounters,
  boldFolder, boldSubfolder, boldMatter, seenAtLoad, markSeen,
  folderRecent, affectedMode, affectedMatterIds, affectedFolderIds,
}: {
  node: FolderNode
  openSet: Set<number>
  onToggle: (id: number) => void
  mattersByFolder: Map<number, Matter[]>
  selectedMatterId: number | 'inbox' | null
  selectedFolderId: number | null
  onSelectMatter: (matterId: number, folderId: number | null) => void
  onArchiveMatter?: (matterId: number) => void
  /** True for top-level rows. Removes left padding so the chevron sits
   *  flush with the sidebar edge (saves horizontal space which is
   *  precious in a folder-tree sidebar). */
  isTop?: boolean
  /** When searching, dim folders with 0 hits to opacity-50 so the
   *  tree structure stays visible but the empty-result state is
   *  clear. The chip count itself comes from folderDistinctMatters
   *  which is filter-aware (matters prop is filtered upstream). */
  searchActive?: boolean
  /** Per-folder Set of distinct matter IDs (self + descendants).
   *  Replaces server-side matter_count_recursive which double-counts
   *  matters filed in a parent folder AND one of its sub-folders. */
  folderDistinctMatters?: Map<number, Set<number>>
  /** Per-folder subtree window counters: {issues, newMatters} since border. */
  folderCounters?: Map<number, { issues: number; newMatters: number }>
  /** Per-matter window counters {issues, attachments, new} for leaf rows. */
  matterCounters?: Record<string, MatterCounter>
  /** QA-controlled boldness for top-level folder / sub-folder / matter labels. */
  boldFolder?: boolean
  boldSubfolder?: boolean
  boldMatter?: boolean
  /** Page-load snapshot used to flag rows that appeared after the page
   *  mounted. Forwarded down to children + leaf rows. */
  seenAtLoad?: Set<number> | null
  /** Callback that promotes an id into seenAtLoad after its row has
   *  flashed once. Forwarded down to children + leaf rows. */
  markSeen?: (id: number) => void
  /** Map<folderId, msTimestamp> of the most-recent activity across
   *  each folder's subtree. Orders folders in the "What's New" view. */
  folderRecent?: Map<number, number>
  /** Ken 2026-08-17 "What's New" view: when affectedMode, prune to only
   *  affected folders/matters (issues>0 since the border), force-open the
   *  path, and order by counters.last_activity. */
  affectedMode?: boolean
  affectedMatterIds?: Set<number>
  affectedFolderIds?: Set<number>
}) {
  // "What's New" view force-opens every folder on the affected path (incl.
  // top-level) so the pruned list is fully visible; otherwise honour openSet.
  const isOpen = (
    affectedMode
    || openSet.has(node.id)
  )
  // In "What's New" mode, only surface matters actually affected in the window.
  const matters = affectedMode
    ? (mattersByFolder.get(node.id) || []).filter((m) => affectedMatterIds?.has(m.id))
    : (mattersByFolder.get(node.id) || [])
  const displayedCount = folderDistinctMatters?.get(node.id)?.size ?? 0
  // Two at-rest folder numbers (non-search only), rolled up over the subtree:
  // email-issues added and new matters instantiated since the global border.
  const fc = folderCounters?.get(node.id)
  // Collapsed-only (Ken 2026-08-18): the folder number is a peek at HIDDEN
  // activity, so drop it once the folder is expanded (you can see the matters)
  // — which also removes it throughout the What's New view, where everything
  // is force-open. Pruning still uses folderCounters directly, so unaffected.
  const cIssues = (searchActive || isOpen) ? 0 : (fc?.issues ?? 0)
  const cNew = searchActive ? 0 : (fc?.newMatters ?? 0)
  // During search, dim the NAME to muted (not the whole row — the '0' match
  // chip has to stay legible; the zeroes are the "where not to look" map).
  const dimName = searchActive && displayedCount === 0
  // Accessible label on the toggle: reflects match count during search, else
  // the folder total plus whatever it gained in the window.
  const slotLabel = searchActive
    ? `${node.name}, ${displayedCount} match${displayedCount === 1 ? '' : 'es'}`
    : `${node.name}, ${displayedCount} matter${displayedCount === 1 ? '' : 's'}`
      + (cIssues ? `, ${cIssues} new issue${cIssues === 1 ? '' : 's'}` : '')
      + (cNew ? `, ${cNew} new matter${cNew === 1 ? '' : 's'}` : '')
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(node.name)
  const [newSubOpen, setNewSubOpen] = useState(false)
  // Manage menu for Client Activity rows (Ken 2026-10-22, playhead only) — the
  // single three-dot button (Rename… / Archive). Escape closes it.
  const [folderManageOpen, setFolderManageOpen] = useState(false)

  const update = useMutation({
    mutationFn: (payload: { name?: string; description?: string }) => foldersApi.update(node.id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folders', 'tree'] })
      qc.invalidateQueries({ queryKey: ['matters'] })
    },
  })
  const remove = useMutation({
    mutationFn: () => foldersApi.remove(node.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['folders', 'tree'] })
      // Also invalidate matters — a deleted folder removes its M2M rows,
      // and matters losing their last folder become "Unfiled" in the
      // sidebar render.
      qc.invalidateQueries({ queryKey: ['matters'] })
    },
  })
  const create = useMutation({
    mutationFn: (payload: { name: string }) =>
      foldersApi.create({ name: payload.name, parent_folder: node.id }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['folders', 'tree'] }),
  })
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const { revealed, rowProps } = useDwellReveal()
  // Top-level category pip reveals only after a 4s rest on the row (Ken 2026-11-01).
  const { shown: pipShown, onEnter: onPipEnter, onLeave: onPipLeave } = usePipHoverReveal(PIP_DWELL_MS)
  const fauth = useAuth()
  const playhead = fauth.status === 'authenticated' && isPlayheadTenant(fauth.user.client?.code)
  // Escape closes the Client Activity Manage menu (outside-click has its own catcher).
  useEffect(() => {
    if (!folderManageOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setFolderManageOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [folderManageOpen])

  // "What's New" prune: a folder with no affected descendant vanishes entirely.
  // (After all hooks — Rules of Hooks.)
  if (affectedMode && !affectedFolderIds?.has(node.id)) return null

  return (
    <div className="mb-0.5">
      {/* isTop: pin the top-level folder header just under the controls row so
          it stays as a "you are here" anchor while a deep folder scrolls under
          it (Emerson 2026-08-31). Opaque surface bg covers the scrolling rows;
          z-[9] sits below the z-10 controls row so it tucks under it, and the
          -1px on top overlaps that border by a hair to avoid a sub-pixel seam.
          Sub-folders are intentionally NOT pinned (isTop is only set at root). */}
      <div
        {...rowProps}
        onMouseEnter={(e) => { rowProps.onMouseEnter(e); if (isTop) onPipEnter() }}
        onMouseLeave={() => { rowProps.onMouseLeave(); if (isTop) onPipLeave() }}
        className={`group/folder w-full flex items-center justify-between rounded-md hover:bg-[color:var(--me-hover)] data-[selected=true]:bg-[color:var(--me-selected)] ${isTop ? 'pl-0 pr-1 py-1 sticky z-[9] bg-[color:var(--me-surface)]' : (playhead ? 'pl-0 pr-1 py-1' : 'px-1 py-1')}`}
        style={isTop ? { top: 'calc(var(--me-controls-h) - 1px)' } : undefined}
        data-selected={selectedFolderId === node.id ? 'true' : undefined}
      >
        {!editing ? (
          <button
            onClick={() => { traceDestination({ type: 'folder', id: node.id, label: node.name }); onToggle(node.id) }}
            // Playhead: drop the browser-default blue focus ring (Ken 2026-10-22,
            // the Kestrel row) — selection reads as the row fill, same as matter rows.
            className={`flex items-center flex-1 min-w-0 ${playhead ? 'focus:outline-none focus-visible:outline-none' : 'gap-1'}`}
            style={playhead ? { paddingLeft: PH_BASE + node.depth * PH_STEP } : undefined}
            aria-label={slotLabel}
          >
            {playhead ? (
              <>
                {/* Carets removed (Ken 2026-10-22) — the whole row toggles; the glyph
                    column carries the indent. EVERY container carries a folder mark
                    (folder mark = container, no mark = matter); see the 7B note below. */}
                <span className="shrink-0 inline-flex items-center justify-center"
                      style={{ width: PH_GLYPH, height: PH_GLYPH, marginRight: PH_GAP, color: '#78716c' }}>
                  {/* UXT-7 7B (+ UXT-5 5D): EVERY container — top-level category AND
                      client activity — uses the SAME stock lucide Folder, 12px #78716c.
                      Level is carried by indent, not by a second glyph. Rendered directly
                      (not via <Glyph name="folder">, whose media:"" slot draws nothing);
                      this retires the last bespoke 'subfolder' mark in pane-1. */}
                  <FolderIcon size={12} />
                </span>
                {/* Pane-1 hierarchy by weight+size (Ken 2026-10-22): the top-level
                    category recedes to a small, muted, semibold LABEL; the Client
                    Activity advances to primary ink at full size. Categories are
                    furniture; matters are content. */}
                {/* Row click now SELECTS/toggles the folder (Ken 2026-10-23) — the
                    name no longer opens chat; use the row's Manage (⋯) "Ask about
                    this…" or the bottom-right circle. Top-level category gets an
                    inline band pill that hugs the label (change C). ONE band state
                    for every category — empty vs populated is carried by the count
                    column, not by dimming the band (Ken 2026-10-23). */}
                <span
                  className={`me-folder-name truncate min-w-0 ${isTop
                    ? 'inline-block uppercase'
                    : `me-client-activity font-normal ${dimName ? 'me-text-mute' : 'me-text'}`}`}
                  style={isTop
                    ? bandPillStyle()
                    : { fontSize: 14 }}
                >{node.name}</span>
                {/* UXT-10 10C: the pip leaves the right gutter and hugs the label
                    with a constant 9px gap. The folder name is a single line that
                    ellipsises; the pip is shrink-0 so the name gives way and the pip
                    is never clipped (10C-2/10C-3). Same gate as before — top-level
                    category pips wait for the 4s rest (pipShown). */}
                {(fc?.issues ?? 0) > 0 && (!isTop || pipShown) && (
                  <span className="shrink-0 ml-[9px] tabular-nums" style={{ color: 'var(--me-count-color, #8E8E8E)' }}>
                    <CounterNum value={fc?.issues ?? 0} tight ken={playhead} />
                  </span>
                )}
              </>
            ) : (
              <>
                <ChevronRight size={11} className={`transition-transform ${isOpen ? 'rotate-90' : ''} me-text-mute shrink-0`} />
                <Glyph name={isTop ? 'folder' : 'subfolder'} size={15} className="me-text-mute shrink-0 mr-1"><FolderIcon size={15} className="me-text-mute shrink-0 mr-1" /></Glyph>
                <span className={`me-folder-name truncate ${(isTop ? boldFolder : boldSubfolder) ? 'font-bold' : 'font-medium'} ${dimName ? 'me-text-mute' : 'me-text'}`}>{node.name}</span>
              </>
            )}
          </button>
        ) : (
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { update.mutate({ name }); setEditing(false) }
              if (e.key === 'Escape') { setName(node.name); setEditing(false) }
            }}
            onBlur={() => { if (name !== node.name) update.mutate({ name }); setEditing(false) }}
            className="me-input text-[12px] flex-1"
          />
        )}
        {playhead ? (
          /* KEN/playhead (Ken 2026-10-22): same right gutter as matter rows so the
             dots + count sit on a CONSTANT right edge at every indent level. Fixed
             30px Manage slot + fixed 22px count column, always reserved. Client
             Activity rows (depth>0) get the Manage button (Rename… / Archive); top-
             level rows (depth 0) reserve the slot empty — no button for now. */
          <div className="shrink-0 flex items-center justify-end pointer-events-none" style={{ height: 26 }}>
            <div
              className={`relative shrink-0 flex items-center justify-center ${folderManageOpen ? 'pointer-events-auto' : ''}`}
              style={{ width: 30 }}
              onMouseLeave={() => setFolderManageOpen(false)}
            >
              {/* Manage (⋯) now on EVERY folder row (Ken 2026-10-23): depth 0
                  (category) gets it for the first time, with "Ask about this…" as
                  its only item; depth>0 (Client Activity) keeps Rename…/Archive
                  under that same first "Ask about this…" item. */}
              <button
                onClick={(e) => { e.stopPropagation(); setFolderManageOpen((v) => !v) }}
                data-open={folderManageOpen ? 'true' : undefined}
                className={`inline-flex items-center justify-center rounded me-text-mute hover:me-text transition-opacity duration-[120ms] ${(revealed || folderManageOpen) ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
                style={{ width: 26, height: 26 }}
                title={node.depth > 0 ? 'Manage this client activity' : `Ask about ${node.name}`}
                aria-label={node.depth > 0 ? 'Manage this client activity' : `Ask about ${node.name}`}
              >
                <Glyph name="manage" size={15}><MoreHorizontal size={15} /></Glyph>
              </button>
              {folderManageOpen && (
                  <div
                    className="absolute right-0 top-full z-30 w-36 rounded border me-border p-1 shadow-lg pointer-events-auto"
                    style={{ background: 'var(--me-bg, #fff)' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      onClick={() => { setFolderManageOpen(false); openSecretaryChat({ type: 'folder', id: node.id }, node.name) }}
                      className="block w-full rounded px-2 py-1 text-left text-[12px] me-text hover:bg-[color:var(--me-selected)]"
                    >
                      Ask about this…
                    </button>
                    {node.depth > 0 && (
                      <>
                        <button
                          onClick={() => { setFolderManageOpen(false); setEditing(true) }}
                          className="block w-full rounded px-2 py-1 text-left text-[12px] me-text hover:bg-[color:var(--me-selected)]"
                        >
                          Rename…
                        </button>
                        <button
                          onClick={() => { setFolderManageOpen(false); setConfirmDeleteOpen(true) }}
                          className="block w-full rounded px-2 py-1 text-left text-[12px] me-text hover:bg-[color:var(--me-selected)]"
                        >
                          Archive
                        </button>
                      </>
                    )}
                  </div>
              )}
            </div>
            {/* UXT-10 10C: the 22px container-pip column is retired — the pip now
                rides inline, 9px after the folder name (see above). This right
                cluster is just the Manage (⋯) slot now. */}
          </div>
        ) : (
        <FolderCountSlot
          searchActive={!!searchActive}
          revealed={revealed}
          matchCount={displayedCount}
          persistentCount={playhead ? displayedCount : undefined}
          issues={cIssues}
          newMatters={cNew}
          tools={
            <>
              {/* Icons (Ken 2026-08-11): top-level folder = add sub-folder (+);
                  sub-folder = archive. Both get rename (pencil). */}
              {node.depth === 0 ? (
                <button
                  onClick={(e) => { e.stopPropagation(); setNewSubOpen(true) }}
                  title="New sub-folder"
                  className="me-text-mute hover:me-text inline-flex items-center px-0.5"
                >
                  <Plus size={12} />
                </button>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); setConfirmDeleteOpen(true) }}
                  title="Archive folder"
                  className="me-text-mute hover:me-text inline-flex items-center px-0.5"
                >
                  <Archive size={12} />
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); setEditing(true) }}
                title="Rename folder"
                className="me-text-mute hover:me-text inline-flex items-center px-0.5"
              >
                <Pencil size={12} />
              </button>
            </>
          }
        />
        )}
      </div>

      {newSubOpen && (
        <div className="ml-4 mt-1">
          <NewFolderInline
            parentId={node.id}
            onSubmit={(n) => { create.mutate({ name: n }); setNewSubOpen(false) }}
            onCancel={() => setNewSubOpen(false)}
          />
        </div>
      )}

      {isOpen && (
        // Children indent: top-level uses pl-3 to nudge matter content
        // ~5px past the folder icon. Sub-folders need pl-4 because the
        // folder icon shifts +16px per level (px-1 + chevron + gap +
        // icon + gap), but children-pl-3 only shifts +12px — so without
        // this bump, sub-folder matters end up flush with their parent's
        // folder icon. pl-4 restores the same +5px visual offset at
        // every depth.
        // Ken 2026-07-05: merge subfolders + matters into ONE ordered
        // list per sortMode. Old behaviour was "subfolders first,
        // then matters." New: interleave and sort together (subfolder
        // treated like a loose matter). Sort keys:
        //   'alpha'  — case-insensitive name
        //   'recent' — descending activity timestamp (folder = max
        //              descendant activity; matter = updated_at)
        <div className={`mt-0.5 ${playhead ? '' : (isTop ? 'pl-3' : 'pl-4')}`}>
          {(() => {
            type Item = { kind: 'folder'; node: FolderNode } | { kind: 'matter'; matter: Matter }
            // In "What's New" mode, drop child folders with no affected descendant.
            const childFolders = affectedMode
              ? (node.children || []).filter((c) => affectedFolderIds?.has(c.id))
              : (node.children || [])
            const items: Item[] = [
              ...childFolders.map((c): Item => ({ kind: 'folder', node: c })),
              ...matters.map((m): Item => ({ kind: 'matter', matter: m })),
            ]
            const nameKey = (it: Item) => (it.kind === 'folder' ? it.node.name : it.matter.name).toLowerCase()
            // Affected view orders matters by when they were last *affected*
            // (counters.last_activity), falling back to updated_at.
            const matterActivityMs = (m: Matter) => {
              const la = matterCounters?.[String(m.id)]?.last_activity
              return la ? Date.parse(la) : (m.updated_at ? Date.parse(m.updated_at) : 0)
            }
            const recentKey = (it: Item) => it.kind === 'folder'
              ? (folderRecent?.get(it.node.id) ?? 0)
              : (affectedMode ? matterActivityMs(it.matter) : (it.matter.updated_at ? Date.parse(it.matter.updated_at) : 0))
            items.sort((a, b) => {
              return affectedMode
                ? recentKey(b) - recentKey(a) || nameKey(a).localeCompare(nameKey(b))
                : nameKey(a).localeCompare(nameKey(b))
            })
            if (items.length === 0) {
              // Playhead (KEN): don't spend a line saying there's nothing here —
              // the band itself is dimmed when empty (Ken 2026-10-23). MOVU keeps
              // the italic "empty" placeholder.
              return (affectedMode || playhead) ? null : <div className="px-2 py-1 text-[10.5px] me-text-mute italic">empty</div>
            }
            return items.map((it) => it.kind === 'folder' ? (
              <FolderRow
                key={`f-${it.node.id}`}
                node={it.node}
                openSet={openSet}
                onToggle={onToggle}
                mattersByFolder={mattersByFolder}
                selectedMatterId={selectedMatterId}
                selectedFolderId={selectedFolderId}
                onSelectMatter={onSelectMatter}
                onArchiveMatter={onArchiveMatter}
                searchActive={searchActive}
                folderDistinctMatters={folderDistinctMatters}
                folderCounters={folderCounters}
                matterCounters={matterCounters}
                boldFolder={boldFolder}
                boldSubfolder={boldSubfolder}
                boldMatter={boldMatter}
                seenAtLoad={seenAtLoad}
                markSeen={markSeen}
                folderRecent={folderRecent}
                affectedMode={affectedMode}
                affectedMatterIds={affectedMatterIds}
                affectedFolderIds={affectedFolderIds}
              />
            ) : (
              <MatterRowCompact
                key={`m-${it.matter.id}@${node.id}`}
                matter={it.matter}
                level={node.depth + 1}
                selected={selectedMatterId === it.matter.id && selectedFolderId === node.id}
                onSelect={() => onSelectMatter(it.matter.id, node.id)}
                isNew={seenAtLoad != null && !seenAtLoad.has(it.matter.id)}
                markSeen={markSeen}
                onArchive={onArchiveMatter ? () => onArchiveMatter(it.matter.id) : undefined}
                counter={matterCounters?.[String(it.matter.id)]}
                boldName={boldMatter}
                searchActive={searchActive}
                showActivityAgo={affectedMode}
              />
            ))
          })()}
        </div>
      )}
      {confirmDeleteOpen && (
        <ConfirmDeleteFolderModal
          folderName={node.name}
          matterCount={node.matter_count_recursive}
          childCount={(node.children || []).length}
          onCancel={() => setConfirmDeleteOpen(false)}
          onConfirm={() => { remove.mutate(); setConfirmDeleteOpen(false) }}
          pending={remove.isPending}
        />
      )}
    </div>
  )
}

/** Generic two-button confirm modal. Used for archive/restore matter
 *  actions and any other small "are you sure?" flow that wants to
 *  match the folder-delete modal's look (instead of native window
 *  confirm dialogs, which look out of place and don't theme). */
export function ConfirmActionModal({
  title, body, confirmLabel, onCancel, onConfirm, pending, danger,
}: {
  title: string
  body: ReactNode
  confirmLabel: string
  onCancel: () => void
  onConfirm: () => void
  pending?: boolean
  danger?: boolean
}) {
  return (
    <div
      className="fixed inset-0 z-[1000] grid place-items-center bg-black/30 p-4"
      onClick={onCancel}
    >
      <div
        className="me-card w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
      >
        <div className="mb-3 text-[14px] font-semibold me-text">{title}</div>
        <div className="text-[12.5px] me-text-dim">{body}</div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onCancel} className="me-btn me-btn-outline" disabled={pending}>
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="me-btn me-btn-primary"
            disabled={pending}
            style={danger ? { background: 'var(--me-stale)' } : undefined}
          >
            {pending ? '…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

function ConfirmDeleteFolderModal({
  folderName, matterCount, childCount, onCancel, onConfirm, pending,
}: {
  folderName: string
  matterCount: number
  childCount: number
  onCancel: () => void
  onConfirm: () => void
  pending: boolean
}) {
  return (
    <div
      className="fixed inset-0 z-[1000] grid place-items-center bg-black/30 p-4"
      onClick={onCancel}
    >
      <div
        className="me-card w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
      >
        <div className="flex items-center gap-2 mb-3">
          <Trash2 size={16} className="text-[color:var(--me-stale)]" />
          <div className="text-[14px] font-semibold me-text">
            Delete folder "{folderName}"?
          </div>
        </div>
        <div className="text-[12.5px] me-text-dim space-y-2">
          <p>Matters won't be deleted:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              Matters that had this as their <em>only</em> folder will move to{' '}
              <span className="me-mono">Unfiled</span>.
            </li>
            <li>
              Matters filed in multiple folders only lose the link to this one.
            </li>
            {childCount > 0 && (
              <li>
                {childCount} sub-folder{childCount === 1 ? '' : 's'} will be reparented
                to this folder's parent (or become top-level).
              </li>
            )}
            {matterCount > 0 && (
              <li>
                Currently <span className="me-mono">{matterCount.toLocaleString()}</span> matter
                {matterCount === 1 ? '' : 's'} filed in this folder
                {childCount > 0 ? ' or its sub-folders' : ''}.
              </li>
            )}
          </ul>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="me-btn me-btn-outline"
            disabled={pending}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="me-btn me-btn-primary"
            disabled={pending}
            style={{ background: 'var(--me-stale)' }}
          >
            {pending ? 'Deleting…' : 'Delete folder'}
          </button>
        </div>
      </div>
    </div>
  )
}

function NewFolderInline({
  parentId, onSubmit, onCancel,
}: {
  parentId: number | null
  onSubmit: (name: string) => void
  onCancel: () => void
}) {
  void parentId
  const [name, setName] = useState('')
  return (
    <div className="me-card p-1.5 mb-1">
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && name.trim()) onSubmit(name.trim())
          if (e.key === 'Escape') onCancel()
        }}
        placeholder="folder name…"
        className="me-input text-[12px]"
      />
      <div className="mt-1 flex justify-end gap-1">
        <button onClick={onCancel} className="me-btn me-btn-outline !text-[10px] !py-0.5">Cancel</button>
        <button
          onClick={() => name.trim() && onSubmit(name.trim())}
          disabled={!name.trim()}
          className="me-btn me-btn-primary !text-[10px] !py-0.5"
        >Create</button>
      </div>
    </div>
  )
}

// Compact relative time for the "What's New" view (Ken 2026-08-17, Phase 2) —
// "just now" / "5m ago" / "2h ago" / "3d ago". The title attribute carries the
// full timestamp. Matches the relativeTime style used in Observability.
function activityAgo(iso: string): string {
  const then = Date.parse(iso)
  if (!then) return ''
  // No "ago" and no "-" prefix — just the terse value (e.g. 45m / 3h / 2d).
  // The full timestamp still shows on hover (Ken 2026-08-19). The gutter is
  // justify-end with the count pinned right, so dropping "ago" slides the value
  // right against the number on its own.
  const secs = Math.max(0, (Date.now() - then) / 1000)
  if (secs < 90) return 'now'
  if (secs < 3600) return `${Math.round(secs / 60)}m`
  if (secs < 86400) return `${Math.round(secs / 3600)}h`
  return `${Math.round(secs / 86400)}d`
}

export function MatterRowCompact({
  matter, selected, onSelect, onArchive, onRestore, archived, isNew, markSeen, counter, boldName, searchActive,
  showActivityAgo, level,
}: {
  matter: Matter
  /** Playhead (KEN) indent level = parent folder depth + 1. Drives the glyph
   *  column on the shared grid. Unused for real tenants. */
  level?: number
  selected: boolean
  onSelect: () => void
  onArchive?: () => void
  onRestore?: () => void
  /** "What's New" view: show a small "2h ago" (from counter.last_activity)
   *  at the right of the name line so the recency gradient is legible. */
  showActivityAgo?: boolean
  /** True when rendering inside the Archived section. Dims the row
   *  and swaps the archive button for a restore button. */
  archived?: boolean
  /** Window counters for this matter — at-rest shows email-issues added +
   *  children attachments added since the global border. */
  counter?: MatterCounter
  /** QA-controlled: render the matter name bold. */
  boldName?: boolean
  /** Suppress the at-rest counters during search (the row IS the result). */
  searchActive?: boolean
  /** True when this matter wasn't present on the page-load snapshot —
   *  i.e. it was created by AI (or another user) while the workspace
   *  has been open. The name flashes once on first appearance. */
  isNew?: boolean
  /** Called once on first mount when isNew=true so the parent can
   *  promote this id out of "new" status — prevents the flash from
   *  re-firing on subsequent unmount/remount (e.g. folder collapse). */
  markSeen?: (id: number) => void
}) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)
  // Manage menu (Ken 2026-10-22, playhead/KEN only) — the single matter-row hover
  // button opens a small text menu (Move to… / Archive). Closes on Escape here;
  // outside-click is handled by a full-screen catcher when the menu is open.
  const [manageOpen, setManageOpen] = useState(false)
  // Delete (Privacy → Delete, Heather 2026-08-19): a Trash menu with two
  // strengths. Self-contained mutations so nothing new threads through the
  // sidebar prop chain.
  const [delMenuOpen, setDelMenuOpen] = useState(false)
  const qcDel = useQueryClient()
  const purgeMut = useMutation({ mutationFn: () => purgeMatter(matter.id), onSuccess: () => qcDel.invalidateQueries() })
  const currentFolderIds = new Set((matter.folder_assignments || []).map((a) => a.folder))
  const code = matter.composite_id || `M-${matter.client_seq.toString().padStart(3, '0')}`
  // Flash on AI updates so the user notices a change happened while they
  // sat on this view. The name flash also fires on first mount when the
  // matter is brand-new (isNew) — surfaces AI-driven matter creation that
  // happens via background poll. See useFlashOnChange.
  const nameFlash = useFlashOnChange(matter.name, matter.id, { flashOnFirstMount: isNew })
  const stageFlash = useFlashOnChange(matter.stage, matter.id)
  // After the introduction-flash plays once, tell the parent this id is
  // "seen" so a later unmount/remount (folder collapse, filter change)
  // doesn't trigger the flash a second time. Mount-only effect; matter.id
  // is stable per row (key is keyed by id), so this fires exactly once.
  useEffect(() => {
    if (isNew && markSeen) markSeen(matter.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  // Escape closes the Manage menu (outside-click handled by its catcher).
  useEffect(() => {
    if (!manageOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setManageOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [manageOpen])
  // Layout matches the original (pre-folder-tree) workspace MatterRow:
  // line 1 = matter name, line 2 = code · type · stage. Summary is NOT
  // shown in the list — too cramped to be useful, and the matter detail
  // panel surfaces it. No title-attribute tooltip either.
  // One number at rest (Ken 2026-08-10): the matter's window issue count.
  const cIssues = !searchActive ? (counter?.issues ?? 0) : 0
  const { revealed, rowProps } = useDwellReveal()
  const mauth = useAuth()
  const mPlayhead = mauth.status === 'authenticated' && isPlayheadTenant(mauth.user.client?.code)
  // scroll-mt-14 clears BOTH pinned rows (controls + sticky top-level folder
  // header, Emerson 2026-08-31) so keyboard-focusing a matter deep in a folder
  // doesn't tuck it under the pinned header.
  return (
    <div {...rowProps} className={`relative group/matter mb-0.5 scroll-mt-14 ${archived ? 'opacity-70' : ''}`}>
      <button
        onClick={() => { markSidebarInteracted(); traceDestination({ type: 'matter', id: matter.id, label: matter.name }); onSelect() }}
        data-selected={selected ? 'true' : undefined}
        data-matter-id={matter.id}
        // Selected state = the --me-selected fill (same as a selected pane-2
        // chronology card). outline-none kills the browser-default blue focus
        // ring (visible on iPad Safari tap) so 'selected' reads as the fill,
        // not a blue border. Emerson 2026-08-01.
        // Reserve right padding when counters show so a long name truncates
        // against them instead of running underneath.
        // Right padding reserves the fixed action slot so the name (flex-1,
        // truncate) never reflows between rest and reveal (Ken 2026-08-10).
        className={`block w-full text-left rounded-md ${mPlayhead ? '' : 'pl-2'} ${mPlayhead ? '' : (showActivityAgo ? 'pr-[140px]' : 'pr-[92px]')} py-1 hover:bg-[color:var(--me-hover)] data-[selected=true]:bg-[color:var(--me-selected)] focus:outline-none focus-visible:outline-none`}
        // Playhead: reserve a fixed right gutter (30px Manage + 22px count + the
        // right-1 offset) so the title truncates in the same spot at rest and on
        // hover — nothing jumps (Ken 2026-10-22).
        // UXT-5 5A: matters have no glyph, but their TEXT still indents one level
        // inside the parent — so add the glyph-gutter width (PH_GLYPH + PH_GAP) that
        // the removed mark used to occupy. Text lands at 32 + level*16 (48 under a
        // category, 64 inside a client activity), never left of the parent.
        // UXT-8 K3: pane-1 no longer rides the reading zoom, so the UXT-6
        // --me-reading-inv un-scaling is removed — plain px (it would now shrink
        // the indent at Large/Largest since there's no zoom to cancel).
        // UXT-10 10C: the 22px count column is retired (the pip is now inline on
        // the name). Only the 30px Manage (⋯) slot stays reserved on the right, so
        // the name's wrap/truncation point still doesn't move between rest and hover.
        style={mPlayhead ? { paddingLeft: PH_BASE + (level ?? 1) * PH_STEP + PH_GLYPH + PH_GAP, paddingRight: 34 } : undefined}
      >
        <div className={`flex items-center ${mPlayhead ? '' : 'gap-1.5'}`}>
          {/* Matter glyph — left of the name (Ken 2026-10-22). No icon today;
              renders only when ui_config.glyphs.matter.media is set. Inline SVG
              inherits the row's colour (currentColor) — no fixed colour here. */}
          {/* UXT-4 B4-3: matters carry NO mark (no mark = a matter; a folder mark
              means a container). KEN drops the glyph + its reserved gutter entirely;
              MOVU keeps its matter Glyph slot unchanged. */}
          {mPlayhead ? null : (
            <Glyph name="matter" size={13} className="shrink-0" />
          )}
          {/* Clicking the matter NAME now SELECTS the row again (Ken 2026-10-23)
              — chat opens only from the Manage (⋯) "Ask about this…" item or the
              bottom-right circle. MOVU click behaviour unchanged. */}
          <span
            // UXT-6 6B-3: KEN matter names wrap to two lines then ellipsis (vertical
            // space scrolls and is free; horizontal is scarce). MOVU keeps one-line truncate.
            className={`me-matter-name me-text ${mPlayhead ? 'line-clamp-2' : 'truncate'} flex-1 min-w-0 ${mPlayhead ? 'font-normal' : (boldName ? 'font-bold' : 'font-medium')} ${nameFlash ? 'me-flash' : ''}`}
            style={mPlayhead ? { fontSize: 14 } : undefined}
          >{matter.name}{/* UXT-10 10C: the pip leaves the right gutter and rides INLINE
              at the end of the name — on a 2-line name it lands at the end of the
              second line (10C-1), with a constant 9px gap. KEN only; MOVU keeps its
              right-hand count box below. */}
            {mPlayhead && cIssues > 0 && !searchActive && (
              <span className="ml-[9px] align-baseline"><CounterNum value={cIssues} tight ken={mPlayhead} /></span>
            )}</span>
        </div>
        {/* me-identifier — hidden when Tweaks → IDENTIFIERS is OFF.
            me-identifier-space keeps the row in layout flow so the
            vertical rhythm between matter names in the sidebar stays
            the same whether identifiers are on or off (Ken 2026-07-08
            followup — visible identifier row height ≈ 14px was
            collapsing when hidden and reducing spacing). */}
        <div className={`me-identifier me-identifier-space mt-0.5 truncate ${mPlayhead ? 'me-chrome-id tabular-nums' : 'me-mono me-text-mute text-[11px]'}`}
             style={mPlayhead ? { paddingLeft: PH_GLYPH + PH_GAP } : undefined}>
          {/* Ken 2026-07-21 — sidebar chip shows `kind` (L3 closed
              enum). Falls back to matter_type for legacy matters
              that predate the kind field OR were pre-seeded (which
              don't have a founding issue).
              Ken 2026-08-11 — the kind segment (incl. its leading
              separator) is wrapped in `me-matter-kind` so the QA toggle
              can hide just the kind while keeping the code + stage. Hidden
              by default even when identifiers are exposed. */}
          {code}<span className="me-matter-kind"> · {matter.kind || matter.matter_type}</span>
          {matter.stage && <> · <span className={stageFlash ? 'me-flash' : ''}>{matter.stage.replace(/_/g, ' ')}</span></>}
        </div>
      </button>
      {/* ROW ACTION SLOT — on the name line, right-aligned, fixed width. The
          number (+ new pip) stays pinned RIGHT and visible at all times; the
          dwell tools reveal to their LEFT so they never cover the count
          (Ken 2026-08-12). Container is pointer-events-none so the count area
          click-through-selects the row; only the revealed tools capture. */}
      {mPlayhead && !archived ? (
        /* KEN/playhead (Ken 2026-10-22): one "Manage" button in a fixed 30px
           gutter (26×26 target). The gutter is always reserved, so the title
           truncation point never moves between rest and hover. Button reveals on
           hover; menu opens left-and-down with text-only Move to… / Archive.
           UXT-10 10C: the 22px count column that used to sit to the Manage slot's
           right is gone — the matter pip now rides inline on the name. */
        <div className="absolute right-1 top-1 flex items-center justify-end pointer-events-none" style={{ height: 26 }}>
          <div
            className={`relative shrink-0 flex items-center justify-center ${manageOpen ? 'pointer-events-auto' : ''}`}
            style={{ width: 30 }}
            // Dismiss on mouse-leave (Ken 2026-10-22) — the menu was sticky. The
            // wrapper captures events only while open; the menu sits flush under the
            // button (top-full) and is a DOM descendant, so moving between them stays
            // "inside" and only leaving both closes it. Escape also closes.
            onMouseLeave={() => setManageOpen(false)}
          >
            <button
              onClick={(e) => { e.stopPropagation(); setManageOpen((v) => !v) }}
              data-open={manageOpen ? 'true' : undefined}
              className={`inline-flex items-center justify-center rounded me-text-mute hover:me-text transition-opacity duration-[120ms] ${(revealed || manageOpen) ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
              style={{ width: 26, height: 26 }}
              title="Manage this matter"
              aria-label="Manage this matter"
            >
              <Glyph name="manage" size={15}><MoreHorizontal size={15} /></Glyph>
            </button>
            {manageOpen && (
                <div
                  className="absolute right-0 top-full z-30 w-36 rounded border me-border p-1 shadow-lg pointer-events-auto"
                  style={{ background: 'var(--me-bg, #fff)' }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => { setManageOpen(false); openSecretaryChat({ type: 'matter', id: matter.id }, matter.name) }}
                    className="block w-full rounded px-2 py-1 text-left text-[12px] me-text hover:bg-[color:var(--me-selected)]"
                  >
                    Ask about this…
                  </button>
                  <button
                    onClick={() => { setManageOpen(false); setPickerOpen(true) }}
                    className="block w-full rounded px-2 py-1 text-left text-[12px] me-text hover:bg-[color:var(--me-selected)]"
                  >
                    Move to…
                  </button>
                  {onArchive && (
                    <button
                      onClick={() => { setManageOpen(false); setArchiveOpen(true) }}
                      className="block w-full rounded px-2 py-1 text-left text-[12px] me-text hover:bg-[color:var(--me-selected)]"
                    >
                      Archive
                    </button>
                  )}
                </div>
            )}
          </div>
        </div>
      ) : (
      <div className={`absolute right-1 top-1 h-[18px] ${showActivityAgo ? 'w-[136px]' : ROW_SLOT_W} flex items-center justify-end gap-2 pointer-events-none`}>
        {/* Tools — LEFT of the number; only opacity animates so the count
            never reflows. The 3px right margin nudges them back left (the
            tight number is narrower than the old fixed column, which had
            shifted them right) — Ken 2026-08-12. */}
        <div
          className={`flex items-center gap-1 mr-[3px] transition-opacity duration-[120ms] ${(revealed || archived) ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        >
          {/* Icons (Ken 2026-08-11): move (→) + archive; restore on archived. */}
          {!archived && (
            <button
              onClick={(e) => { e.stopPropagation(); setPickerOpen(true) }}
              className="me-text-mute hover:me-text inline-flex items-center"
              title="Move this matter to folders"
            >
              <Glyph name="move" size={12}><FolderInput size={12} /></Glyph>
            </button>
          )}
          {onArchive && !archived && (
            <button
              onClick={(e) => { e.stopPropagation(); setArchiveOpen(true) }}
              className="me-text-mute hover:me-text inline-flex items-center"
              title="Archive this matter"
            >
              <Glyph name="archive" size={12}><Archive size={12} /></Glyph>
            </button>
          )}
          {onRestore && archived && (
            <button
              onClick={(e) => { e.stopPropagation(); onRestore() }}
              className="me-text-mute hover:me-text inline-flex items-center"
              title="Restore this matter from archive"
            >
              <RotateCcw size={12} />
            </button>
          )}
          {/* Delete only from Archive — you must archive first (two-step
              discipline, Ken 2026-08-19). No trash on live foldered matters. */}
          {archived && (
            <button
              onClick={(e) => { e.stopPropagation(); setDelMenuOpen((v) => !v) }}
              className="me-text-mute hover:me-text inline-flex items-center"
              title="Delete this matter"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
        {/* Number + new pip — fixed-width, right-aligned box. The number's
            right edge is pinned and the pip hugs it (2px) so it reads as
            modifying the count (Ken 2026-08-12). The FIXED width (not
            content-width) keeps the box the same size whether or not the pip
            is present — and regardless of digit count — so the dwell tools to
            its left always reveal in exactly the same horizontal spot
            (Ken 2026-08-12). The blank sits to the LEFT of the pip, so the
            pip still hugs the digit. */}
        {/* "What's New" recency (Ken 2026-08-17): lives in this right gutter,
            LEFT of the number, so it never eats into the (truncating) name. */}
        {showActivityAgo && counter?.last_activity && (
          <span
            className="shrink-0 text-[10px] me-text-mute tabular-nums whitespace-nowrap"
            title={`Last new issue: ${new Date(counter.last_activity).toLocaleString()}`}
          >
            {activityAgo(counter.last_activity)}
          </span>
        )}
        <div className="flex shrink-0 items-center justify-end gap-[4px] w-10" style={{ color: 'var(--me-count-color, #8E8E8E)' }}>
          {cIssues > 0 && <CounterNum value={cIssues} tight ken={mPlayhead} />}
          {/* NEW pip (Ken 2026-08-17): moved to the RIGHT of the number — it
              read funny hugging the left. Reserved 6px slot so the number
              column stays put whether or not the matter is new. */}
          <span className="shrink-0 w-[6px] flex items-center justify-center">
            {counter?.new && (
              <span
                className="rounded-full"
                style={{ width: 6, height: 6, background: 'var(--me-new-pip)' }}
                title="New within the current window"
                aria-label="new"
              />
            )}
          </span>
        </div>
      </div>
      )}
      {pickerOpen && (
        <FolderPickerModal
          matter={matter}
          currentFolderIds={currentFolderIds}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {archiveOpen && onArchive && (
        <ConfirmActionModal
          title={`Archive "${matter.name}"?`}
          body={<>It moves to the <span className="me-mono">Archived</span> section and stays accessible. You can restore it any time.</>}
          confirmLabel="Archive"
          onCancel={() => setArchiveOpen(false)}
          onConfirm={() => { onArchive(); setArchiveOpen(false) }}
        />
      )}
      {delMenuOpen && (
        <>
          {/* click-catcher to dismiss */}
          <div className="fixed inset-0 z-20" onClick={(e) => { e.stopPropagation(); setDelMenuOpen(false) }} />
          <div
            className="absolute right-1 top-7 z-30 w-48 rounded border me-border p-1 shadow-lg"
            style={{ background: 'var(--me-bg, #fff)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                setDelMenuOpen(false)
                const t = window.prompt(`Permanently purge "${matter.name}"?\n\nThis erases its content and cannot be undone. Type DELETE to confirm.`)
                if (t === 'DELETE') purgeMut.mutate()
              }}
              className="block w-full rounded px-2 py-1 text-left text-[12px] hover:bg-[color:var(--me-selected)]"
              style={{ color: 'var(--me-stale)' }}
            >
              Purge permanently
            </button>
          </div>
        </>
      )}
    </div>
  )
}

// ─── Folder picker (popover for moving a matter into folder(s)) ─────

export function FolderPickerModal({
  matter, currentFolderIds, onClose,
}: {
  matter: Matter
  currentFolderIds: Set<number>
  onClose: () => void
}) {
  const matterId = matter.id
  const tree = useQuery({
    queryKey: ['folders', 'tree'],
    queryFn: () => foldersApi.tree(),
    staleTime: 30_000,
  })
  const qc = useQueryClient()

  const [selected, setSelected] = useState<Set<number>>(new Set(currentFolderIds))
  // Track "new folder" UI visibility for top-level adds.
  const [showNewTop, setShowNewTop] = useState(false)

  const toggleSel = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const create = useMutation({
    mutationFn: (payload: { name: string; parent_folder?: number | null }) => foldersApi.create(payload),
    onSuccess: (folder) => {
      qc.invalidateQueries({ queryKey: ['folders', 'tree'] })
      setSelected((prev) => new Set([...prev, folder.id]))
    },
  })
  const assign = useMutation({
    mutationFn: (folder: number) => foldersApi.assignMatter(matterId, folder),
  })
  const dismiss = useMutation({
    mutationFn: (folder: number) => foldersApi.dismissMatter(matterId, folder),
  })

  // Compute diff vs initial state. Save is only meaningful when there's
  // an actual change (added or removed assignments).
  const hasDiff = useMemo(() => {
    if (selected.size !== currentFolderIds.size) return true
    for (const id of selected) if (!currentFolderIds.has(id)) return true
    return false
  }, [selected, currentFolderIds])

  const apply = async () => {
    const toAdd = [...selected].filter((id) => !currentFolderIds.has(id))
    const toRemove = [...currentFolderIds].filter((id) => !selected.has(id))
    await Promise.all([
      ...toAdd.map((id) => assign.mutateAsync(id)),
      ...toRemove.map((id) => dismiss.mutateAsync(id)),
    ])
    // refetchQueries (not just invalidate) so the workspace's matter list
    // and the picker's tree both refresh BEFORE the popover closes.
    await Promise.all([
      qc.refetchQueries({ queryKey: ['matters'] }),
      qc.refetchQueries({ queryKey: ['folders', 'tree'] }),
    ])
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-[1000] grid place-items-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="me-card w-full max-w-sm p-3 shadow-lg"
        onClick={(e) => e.stopPropagation()}
        style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}
      >
        <div className="mb-2">
          <div className="text-[13px] font-semibold me-text">File matter in folders</div>
          <div className="me-text-mute text-[11px] truncate" title={matter.name}>
            <span className="me-mono">{matter.composite_id}</span> · {matter.name}
          </div>
        </div>
      <div className="max-h-72 overflow-auto pr-1">
        {tree.data && (
          <FolderPickerList
            nodes={tree.data}
            selected={selected}
            onToggle={toggleSel}
            onCreateChild={(parentId, name) => create.mutate({ name, parent_folder: parentId })}
          />
        )}
      </div>
      <div className="mt-2 border-t me-border pt-1.5">
        {!showNewTop ? (
          <button
            onClick={() => setShowNewTop(true)}
            className="me-btn me-btn-outline !text-[11px] w-full justify-center"
          >
            <Plus size={10} /> New top-level folder
          </button>
        ) : (
          <NewFolderInline
            parentId={null}
            onSubmit={(name) => { create.mutate({ name, parent_folder: null }); setShowNewTop(false) }}
            onCancel={() => setShowNewTop(false)}
          />
        )}
      </div>
      <div className="mt-2 flex justify-end gap-1.5">
        <button onClick={onClose} className="me-btn me-btn-outline !text-[11px]">Cancel</button>
        <button
          onClick={apply}
          disabled={!hasDiff || assign.isPending || dismiss.isPending}
          className="me-btn me-btn-primary !text-[11px]"
          title={!hasDiff ? 'No changes to save' : ''}
        >
          {assign.isPending || dismiss.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
      </div>
    </div>
  )
}

function FolderPickerList({
  nodes, selected, onToggle, onCreateChild, indent = 0,
}: {
  nodes: FolderNode[]
  selected: Set<number>
  onToggle: (id: number) => void
  onCreateChild: (parentId: number, name: string) => void
  indent?: number
}) {
  return (
    <div>
      {nodes.map((n) => (
        <FolderPickerRow
          key={n.id}
          node={n}
          selected={selected}
          onToggle={onToggle}
          onCreateChild={onCreateChild}
          indent={indent}
        />
      ))}
    </div>
  )
}

function FolderPickerRow({
  node, selected, onToggle, onCreateChild, indent,
}: {
  node: FolderNode
  selected: Set<number>
  onToggle: (id: number) => void
  onCreateChild: (parentId: number, name: string) => void
  indent: number
}) {
  const [showAdd, setShowAdd] = useState(false)
  const canHaveChildren = node.depth < 2
  return (
    <div style={{ paddingLeft: indent * 12 }}>
      <label
        className="mb-0.5 cursor-pointer flex items-center gap-1.5 group/pickerrow hover:bg-[color:var(--me-hover)] rounded-md px-2 py-1.5"
      >
        <input
          type="checkbox"
          checked={selected.has(node.id)}
          onChange={() => onToggle(node.id)}
          className="cursor-pointer shrink-0"
        />
        <FolderIcon size={11} className="me-text-mute shrink-0" />
        <span className="text-[11.5px] me-text truncate flex-1 min-w-0">{node.name}</span>
        {/* AI-created sparkle removed per Ken 2026-05-25 (callout 3). */}
        {canHaveChildren && (
          <button
            onClick={(e) => { e.preventDefault(); setShowAdd(true) }}
            className="hidden group-hover/pickerrow:inline-flex items-center text-[10px] me-text-mute shrink-0"
            title="Add sub-folder"
          >
            <Plus size={10} />
          </button>
        )}
      </label>
      {showAdd && (
        <div style={{ paddingLeft: 16 }}>
          <NewFolderInline
            parentId={node.id}
            onSubmit={(name) => { onCreateChild(node.id, name); setShowAdd(false) }}
            onCancel={() => setShowAdd(false)}
          />
        </div>
      )}
      {(node.children || []).length > 0 && (
        <FolderPickerList
          nodes={node.children}
          selected={selected}
          onToggle={onToggle}
          onCreateChild={onCreateChild}
          indent={indent + 1}
        />
      )}
    </div>
  )
}
